// Utility functions for managing books in local storage
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  defaultBookMeta,
  defaultBookSettings,
  defaultFavorite,
  defaultFontSize,
  defaultPrefs,
  defaultStatusBarBoxes,
} from "./defaults";
import localforage from "localforage";

dayjs.extend(utc);

import type {
  BookMeta,
  BookSettings,
  NoteCommentGroup,
  Note,
  NotesGroup,
  Person,
  Prefs,
  StatusBarBoxInfo,
  UpdateRecord,
  UserInfo,
  NoteComment,
  BookCollection,
  NotesStatus,
  NoteStatus,
  SnackbarMessage,
  GeepersQuery,
  Misc,
  AnchorLocations,
  RichLocation,
  RichSection,
  ChaptersInfo,
  Group,
} from "../types/book";
import {
  saveObject,
  loadObject,
  calculateFileHashFromArrayBuffer,
  calculateFileHash,
  saveFile,
  deleteFile,
  loadFile,
  calculateHashFromString,
} from "./indexedDB";
import { EpubCFI, createBook } from "../epub";
import type { Annotations, ParsedBook } from "../epub";
import {
  aiUser,
  localStorageBooksName,
  localStorageCurrentUserName,
  localStorageFollowingName,
  localStorageMiscName,
  localStorageNotesStatusName,
  locationSize,
} from "../config";
import { debounce } from "./core";
import { isIOS, isDesktop } from "react-device-detect";
import { logger } from "./logger";
import daynight from "daynight";
import { useOnlineStore } from "../store/onlineStore";
import { countComments, mergeNotes } from "./notes";
import { MIN_UPDATE_INTERVAL } from "./constants";
import { createDeterministicUUIDv4 } from "./crypto";
import { setViewport } from "./viewports";
import { getRootGroup } from "./groups";
import { backfillBookIdentityCollection, ensureBookIdentityFields } from "./bookIdentity";
import {
  resolveHistoryBackTarget,
  resolveHistoryForwardTarget,
} from "./readingHistory";
import {
  buildBookComparisonFieldsFromSections,
  hasCurrentBookComparisonFields,
  summarizeBookComparisonForDebug,
  type BookComparisonFields,
} from "./bookComparison";
import {
  buildHighlightReactionAnchorSelectors,
  calculateReactionBadgeX,
  emojiToUnified,
  escapeSelectorAttrValue,
  findHighlightReactionAnchorGroup,
  getAppleEmojiImageUrl,
  getReactionBadgeMetrics,
  getReactionSignature,
  resolveReactionBadgeClampBounds,
  resolveReactionBadgePreferredPlacement,
  sortHighlightRects,
} from "./highlightReactions";
import { cssHasPublisherFontFamily } from "./readerTypography";
import { isNativeApp } from "./platform";
import { recordLocalDriveBookDeletion } from "./driveSyncState";
import {
  getCfiRangeEndpointOrder,
  isCfiRangeEndpointOrderValid,
  isCfiRangeEndpointOrderValidForDocument,
} from "./cfiRange";
import {
  getReadingPositionTimestamp,
  loadReaderPositionCheckpoint,
  reconcileReaderPositionCheckpoint,
} from "./readerPositionCheckpoint";

// Import group functions as needed to avoid circular dependencies
// These will be initialized after our functions are defined
let groupUtils: {
  addItemToGroup: (groupId: string, itemId: string) => boolean;
  removeItemFromGroup: (groupId: string, itemId: string) => boolean;
  getGroups: () => { [id: string]: Group };
} | null = null;

/** A book usable for parse-level operations. */
type AnyBook = ParsedBook;

export type BookRemoteUpdate = Pick<BookMeta, "id"> &
  Partial<
    Omit<BookMeta, "id" | "settings" | "notes" | "visibleAt" | "minutesRead">
  > & {
    settings?: Partial<BookMeta["settings"]>;
    notes?: NotesGroup;
    visibleAt?: NonNullable<BookMeta["visibleAt"]>;
    minutesRead?: NonNullable<BookMeta["minutesRead"]>;
  };

// Function to initialize group utilities
export const initGroupUtils = (utils: typeof groupUtils) => {
  groupUtils = utils;
};

// Initialize localforage
localforage.config({
  name: "WeReader",
  storeName: "ePubData",
});

const chaptersStore = localforage.createInstance({
  name: "WeReader",
  storeName: "chapters",
});

const anchorsStore = localforage.createInstance({
  name: "WeReader",
  storeName: "anchors",
});

const chapterLocsStore = localforage.createInstance({
  name: "WeReader",
  storeName: "chapterLocs",
});

const cssStore = localforage.createInstance({
  name: "WeReader",
  storeName: "css",
});

let cachedBooks: BookCollection | null = null;
let cachedNotesStatus: NotesStatus | null = null;
let cachedCurrentUser: UserInfo | null = null;
let cachedFollowing: Person[] | null = null;
let cachedMisc: Misc | null = null;
let inFlightUpgrade: Promise<number | undefined> | null = null;

export const loadFromLocalForage = async (): Promise<boolean> => {
  if (cachedBooks === null) {
    cachedBooks = await localforage.getItem(localStorageBooksName);
  }
  if (cachedNotesStatus === null) {
    cachedNotesStatus = await localforage.getItem(localStorageNotesStatusName);
  }
  if (cachedCurrentUser === null) {
    cachedCurrentUser = await localforage.getItem(localStorageCurrentUserName);
  }
  if (cachedFollowing === null) {
    cachedFollowing = await localforage.getItem(localStorageFollowingName);
  }
  if (cachedMisc === null) {
    cachedMisc = await localforage.getItem(localStorageMiscName);
  }
  //   const allGroupIds = getAllGroupIds(cachedCurrentUser?.prefs?.groups);
  const rootGroup = getRootGroup(cachedCurrentUser?.prefs?.groups);
  const archivedShelfId = Object.values(
    cachedCurrentUser?.prefs?.groups || {},
  ).find((group) => group.name === "Archived")?.id;
  const activeShelfId = Object.values(
    cachedCurrentUser?.prefs?.groups || {},
  ).find((group) => group.name === "Active")?.id;
  let updated = false;
  let recoveredReaderPositions = 0;

  const hasRootAsAncestor = (book: BookMeta) => {
    if (!book.parentId) {
      return false;
    }
    //recursively follow parentIDs until root, else false
    let item = book as Group | BookMeta | undefined;
    while (item?.parentId) {
      item = cachedCurrentUser?.prefs?.groups?.[item.parentId];
      if (!item) {
        return false;
      }
    }
    return item?.id === "root";
  };

  if (cachedBooks) {
    for (const book of Object.values(cachedBooks)) {
      let workingBook = book;
      const checkpoint = loadReaderPositionCheckpoint(book.id);
      const reconciliation = reconcileReaderPositionCheckpoint(
        book.settings,
        checkpoint,
      );
      if (reconciliation.applied) {
        workingBook = {
          ...book,
          timestamp: Math.max(
            Number(book.timestamp) || 0,
            reconciliation.checkpointTimestamp,
          ),
          settings: reconciliation.settings,
        };
        cachedBooks[book.id] = workingBook;
        recoveredReaderPositions += 1;
        updated = true;
        logger.info("[reader-position-checkpoint] recovered newer position", {
          bookId: book.id,
          title: book.title,
          persistedTimestamp: reconciliation.persistedTimestamp,
          checkpointTimestamp: reconciliation.checkpointTimestamp,
          location: reconciliation.settings.location || null,
          progress: reconciliation.settings.progress ?? null,
        });
      }
      if (workingBook.location === "active" && activeShelfId && !workingBook.parentId) {
        cachedBooks[book.id] = {
          ...workingBook,
          parentId: activeShelfId,
          parentUpdatedAt: Date.now(),
        };
        updated = true;
      } else if (
        (workingBook.location === "archived" ||
          workingBook.location === "archive" ||
          workingBook.archived) &&
        archivedShelfId &&
        !workingBook.parentId
      ) {
        cachedBooks[book.id] = {
          ...workingBook,
          parentId: archivedShelfId,
          parentUpdatedAt: Date.now(),
        };
        updated = true;
      } else if (
        workingBook.location !== "trash" &&
        !hasRootAsAncestor(workingBook)
      ) {
        console.log(
          "found loose book, setting location to root group:",
          workingBook.parentId,
          "book title:",
          workingBook.title,
        );
        if (rootGroup) {
          cachedBooks[book.id] = {
            ...workingBook,
            parentId: rootGroup.id,
            parentUpdatedAt: Date.now(),
          };
          updated = true;
        } else {
          console.warn("Root group not found, cannot update book location");
        }
      }
    }
  }
  if (updated && cachedBooks) {
    updateBooksCache(cachedBooks);
  }
  if (recoveredReaderPositions > 0) {
    logger.info("[reader-position-checkpoint] recovery complete", {
      recoveredReaderPositions,
    });
  }
  console.info("loaded from localForage:", cachedMisc);
  return true;
};

export const getBooks = (parentId?: string): BookCollection => {
  const books = cachedBooks || {};
  if (parentId) {
    return Object.fromEntries(
      Object.entries(books).filter(([id]) => books[id].parentId === parentId),
    );
  }
  return books;
};

export const pruneTrashEntries = (): void => {
  const books = getBooks();
  let pruned = false;

  for (const id in books) {
    if (books[id].location === "trash") {
      delete books[id];
      pruned = true;
    }
  }

  if (pruned) {
    try {
      updateBooksCache(books);
      console.log("Pruned trash entries from books cache");
    } catch (error) {
      console.error("Error pruning trash entries:", error);
    }
  }
};

export const updateBooksCache = (books: BookCollection, reason = "books-cache") => {
  try {
    cachedBooks = books;
    localforage.setItem(localStorageBooksName, books);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("wereader:local-data-changed", {
          detail: { reason },
        }),
      );
    }
  } catch (error) {
    // if (error instanceof DOMException && error.name === 'QuotaExceededError') {
    //   const usedSpace = await calculateUsedStorage();
    //   console.warn(`Storage quota exceeded. Attempting to prune trash entries. Current usage: ${usedSpace}`);

    //   pruneTrashEntries();
    //   const updatedUsedSpace = await calculateUsedStorage();

    //   // Try to update the cache again after pruning
    //   try {
    //     localforage.setItem(localStorageBooksName, books);
    //     console.log('Successfully updated books cache after pruning trash entries');
    //     window.dispatchEvent(
    //       new CustomEvent('snackbarMessage', {
    //         detail: `Storage quota was exceeded. Pruned trash entries.\n\nStorage was ${usedSpace} but is now ${updatedUsedSpace}.`,
    //       })
    //     );
    //   } catch (retryError) {
    //     const message = `Storage quota still exceeded after pruning. Current usage: ${updatedUsedSpace}. Please delete some books manually to free up space.`;
    //     console.error(message);
    //     window.dispatchEvent(new CustomEvent('snackbarMessage', { detail: message }));
    //   }
    // } else {
    console.error("Error updating books cache:", error);
    // }
  }
};

// const calculateUsedStorage = async (): Promise<string> => {
//   let total = 0;
//   const keys = await localforage.keys();
//   for (let key of keys) {
//       total += (await localforage.getItem(key) as string).length * 2; // Multiply by 2 for UTF-16 encoding
//   }
//   return (total / 1024 / 1024).toFixed(2) + ' MB';
// };

export const getNotesStatus = (): NotesStatus => {
  return cachedNotesStatus || {};
};

export const getCurrentUser = (): UserInfo | null => {
  return cachedCurrentUser || null;
};

export const getFollowing = (): Person[] | null => {
  return cachedFollowing || null;
};

export const getMisc = (): Misc | null => {
  return cachedMisc || null;
};

export const updateNotesStatus = (
  notesStatus: NotesStatus,
  reason = "notes-status",
) => {
  cachedNotesStatus = notesStatus;
  localforage.setItem(localStorageNotesStatusName, notesStatus);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("wereader:local-data-changed", {
        detail: { reason },
      }),
    );
  }
};

export const updateCurrentUser = (
  user: UserInfo,
  reason = "current-user",
) => {
  cachedCurrentUser = user;
  console.log("updatedCurrentUser:", user);
  localforage.setItem(localStorageCurrentUserName, user);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("wereader:local-data-changed", {
        detail: { reason },
      }),
    );
  }
  return user;
};

export const updateFollowing = (
  following: Person[],
  reason = "following",
) => {
  cachedFollowing = following;
  localforage.setItem(localStorageFollowingName, following);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("wereader:local-data-changed", {
        detail: { reason },
      }),
    );
  }
};

export const updateMisc = (misc: Misc, reason = "misc") => {
  cachedMisc = misc;
  localforage.setItem(localStorageMiscName, misc);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("wereader:local-data-changed", {
        detail: { reason },
      }),
    );
  }
};

/**
 * Adds default books for new users, including translations.
 * Downloads and adds The Metamorphosis in multiple languages.
 * @returns Promise that resolves when all books are added
 */
/**
 * Configuration for default books to add
 */
const DEFAULT_BOOKS = [
  {
    lang: "en",
    title: "(en) Franz Kafka - Metamorphosis.epub",
  },
  //   {
  //     lang: 'de',
  //     title: '(de) Franz Kafka - Die Verwandlung.epub',
  //   },
  //   {
  //     lang: 'eo',
  //     title: '(eo) Franz Kafka - La Metamorfozo.epub',
  //   },
] as const;

/**
 * Adds default books for new users, including translations.
 * Downloads and adds The Metamorphosis in multiple languages.
 * @returns Promise that resolves when all books are added
 */
export const addDefaultBooks = async (): Promise<void> => {
  try {
    console.log("adding default books");

    // Helper function to create File from URL
    const createBookFile = async (
      url: string,
      filename: string,
    ): Promise<File> => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${filename}: ${response.status}`);
      }
      const blob = await response.blob();
      console.log(`Downloaded ${filename}, size: ${blob.size} bytes`);
      return new File([blob], filename, { type: "application/epub+zip" });
    };

    // Add all books and collect their IDs
    await Promise.all(
      DEFAULT_BOOKS.map(async ({ title }) => {
        const file = await createBookFile(
          `${import.meta.env.BASE_URL}files/${title}`,
          title,
        );
        const id = await addBookFromFile(file);
        if (!id) {
          console.error(`Failed to add ${title}`);
        }
        return id;
      }),
    );

    // Create translations mapping
    // const translations = DEFAULT_BOOKS.reduce((acc, { lang }, index) => {
    //   if (bookIds[index]) {
    //     acc[lang] = bookIds[index];
    //   }
    //   return acc;
    // }, {} as Record<string, string>);

    // Update all books with translations
    // for (const { lang } of DEFAULT_BOOKS) {
    //   const bookId = translations[lang];
    //   const book = getBook(bookId);
    //   if (book) {
    //     await updateBook({ ...book, translations }, true);
    //   }
    // }

    // console.log('added default books:', translations);
  } catch (error) {
    console.error("Error adding default books:", error);
  }
};

export const getNoteStatus = (noteId: string): NoteStatus | undefined => {
  return getNotesStatus()?.[noteId];
};

export const setNoteStatus = (noteId: string, noteStatus: NoteStatus) => {
  const notesStatus = getNotesStatus();
  notesStatus[noteId] = { timestamp: Date.now(), ...noteStatus };
  updateNotesStatus(notesStatus);
};

export const clearCurrentUser = () => {
  cachedCurrentUser = null;
  localforage.removeItem(localStorageCurrentUserName);
  console.log("cleared current user");
};

export const getTheme = (theme?: string): "light" | "dark" => {
  const currentTheme = theme || getPrefs().theme;
  return currentTheme === "auto"
    ? daynight().light
      ? "light"
      : "dark"
    : (currentTheme as "light" | "dark") ||
        (window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light");
};

export const getCommentList = (
  commentGroup?: NoteCommentGroup,
): NoteComment[] => {
  if (!commentGroup) {
    return [];
  }

  const comments = Object.values(commentGroup).filter(
    (comment) => !comment.deleted,
  );
  if (comments.length === 0) return [];

  return comments.sort((a, b) => {
    // If either comment is missing a timestamp, it should come first
    if (!a.timestamp) return -1;
    if (!b.timestamp) return 1;

    // Otherwise, sort by timestamp
    const dateA = dayjs(a.timestamp).valueOf();
    const dateB = dayjs(b.timestamp).valueOf();
    return dateA - dateB; // Earliest to most recent
  });
};

export const fetchGeepersData = async (data: GeepersQuery) => {
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return;
  }
  if (!getCurrentUser()) {
    return Promise.resolve({});
  }

  const summarizeForLog = (value: string | undefined, maxLength = 180) => {
    const normalized = (value || '').replace(/\s+/g, ' ').trim();
    if (!normalized) {
      return '';
    }
    if (normalized.length <= maxLength) {
      return normalized;
    }
    const headLength = Math.max(0, Math.floor((maxLength - 3) * 0.7));
    const tailLength = Math.max(0, maxLength - 3 - headLength);
    return `${normalized.slice(0, headLength)}...${normalized.slice(
      normalized.length - tailLength,
    )}`;
  };

  const summarizedContext = Object.fromEntries(
    Object.entries(data.context || {}).map(([label, value]) => [
      label,
      {
        length: value.length,
        preview: summarizeForLog(value),
      },
    ]),
  );

  console.info('[geepers] request payload', {
    command: data.command,
    lang: data.lang,
    textLength: (data.text || '').length,
    textPreview: summarizeForLog(data.text),
    queryLength: (data.query || '').length,
    queryPreview: summarizeForLog(data.query),
    contextLabels: Object.keys(data.context || {}),
    context: summarizedContext,
  });

  try {
    const response = await fetch("/api/geepers", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    const result = await response.json();
    return result["response"];
  } catch (error) {
    console.error("Error fetching data:", error);
    throw error;
  }
};

// const deleteCookie = (name = 'session') => {
//   document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
//   console.log('deleted cookie: ', name);
// };

// function getDifferences<T extends object>(newObj: T, oldObj: T): Partial<T> {
//   const diff: Partial<T> = {};
//   (Object.keys(newObj) as Array<keyof T>).forEach((key) => {
//     // Using a safer approach to check for property existence
//     if (Object.prototype.hasOwnProperty.call(oldObj, key) && newObj[key] !== oldObj[key]) {
//       diff[key] = newObj[key];
//     }
//   });
//   return diff;
// }

const polishBook = (book: BookMeta) => {
  if (!book.notes) {
    book.notes = {};
  }
  const email = getCurrentUser()?.email;
  Object.values(book.notes).forEach((note) => {
    if (email && !note.owner) {
      note.owner = email;
    } else if (note.owner === undefined) {
      note.owner = "";
    }
  });
};

const stripDerivedTextMetadata = (book: BookMeta): BookMeta => ({
  ...book,
  chapterHashes: undefined,
  comparisonVersion: undefined,
  normalizedTextHash: undefined,
  coreTextHash: undefined,
  shingleFingerprint: undefined,
  normalizedSectionHashes: undefined,
  textTokenCount: undefined,
  spineItemCount: undefined,
});

const hasDifferentIdentityFields = (left: BookMeta, right: BookMeta): boolean =>
  left.workId !== right.workId ||
  left.editionId !== right.editionId ||
  left.normalizedTitle !== right.normalizedTitle ||
  left.normalizedAuthor !== right.normalizedAuthor;

const loadComparisonSections = async (
  book: AnyBook,
): Promise<Pick<RichSection, "href" | "html" | "text">[]> => {
  await book.ready;

  const sections: Pick<RichSection, "href" | "html" | "text">[] = [];
  for (const entry of book.packaging.spine as any[]) {
    const sectionDoc = (await book.load(entry.href)) as Document | undefined;
    if (!sectionDoc) {
      continue;
    }

    sections.push({
      href: entry.href,
      html: sectionDoc.documentElement.outerHTML,
      text: sectionDoc.documentElement.textContent || "",
    });
  }

  return sections;
};

const deriveComparisonFieldsForBook = async (
  book: AnyBook,
): Promise<BookComparisonFields> => {
  const sections = await loadComparisonSections(book);
  return buildBookComparisonFieldsFromSections(sections);
};

type MergeBookMetaOptions = {
  preferExistingReadingPositionOnTie?: boolean;
};

const mergeBookMeta = (
  oldBook: Partial<BookMeta> = {},
  newBook: Partial<BookMeta> = {},
  options: MergeBookMetaOptions = {},
): BookMeta => {
  const oldNotes = oldBook.notes || {};
  const newNotes = newBook.notes || {};
  const latestCover = (oldBook.coverUpdatedAt || 0) > (newBook.coverUpdatedAt || 0) ? oldBook : newBook;

  return {
    ...oldBook,
    ...newBook,
    ...((oldBook.coverUpdatedAt || newBook.coverUpdatedAt) && {
      coverUpdatedAt: latestCover.coverUpdatedAt,
      coverImageHash: latestCover.coverImageHash,
    }),
    settings: mergeBookSettings(oldBook.settings, newBook.settings, options),
    notes: mergeNotes(oldNotes, newNotes),
    visibleAt: {
      ...oldBook.visibleAt,
      ...newBook.visibleAt,
    },
    minutesRead: {
      ...oldBook.minutesRead,
      ...newBook.minutesRead,
    },
    chapterHashes: newBook.chapterHashes || oldBook.chapterHashes || [],
    shingleFingerprint: newBook.shingleFingerprint || oldBook.shingleFingerprint || [],
    normalizedSectionHashes:
      newBook.normalizedSectionHashes || oldBook.normalizedSectionHashes || [],
  } as BookMeta;
};

export const summarizeBookForDebug = (book?: Partial<BookMeta> | null) => ({
  id: book?.id,
  title: book?.title,
  hash: book?.hash,
  workId: book?.workId,
  editionId: book?.editionId,
  progress: book?.settings?.progress,
  location: book?.settings?.location,
  locationUpdatedAt: book?.settings?.locationUpdatedAt,
  parentId: book?.parentId,
  parentUpdatedAt: book?.parentUpdatedAt,
  notesCount: book?.notes ? Object.keys(book.notes).length : 0,
  updatesCount: book?.settings?.updates?.length || 0,
  visibleAtCount: book?.visibleAt ? Object.keys(book.visibleAt).length : 0,
  ...summarizeBookComparisonForDebug(book),
  timestamp: book?.timestamp,
});

const cloneRemoteBookUpdate = (
  bookUpdate: BookRemoteUpdate,
): BookRemoteUpdate => ({
  ...bookUpdate,
  settings: bookUpdate.settings ? { ...bookUpdate.settings } : undefined,
  notes: bookUpdate.notes ? { ...bookUpdate.notes } : undefined,
  visibleAt: bookUpdate.visibleAt ? { ...bookUpdate.visibleAt } : undefined,
  minutesRead: bookUpdate.minutesRead
    ? { ...bookUpdate.minutesRead }
    : undefined,
  chapterHashes: bookUpdate.chapterHashes
    ? [...bookUpdate.chapterHashes]
    : undefined,
  shingleFingerprint: bookUpdate.shingleFingerprint
    ? [...bookUpdate.shingleFingerprint]
    : undefined,
  normalizedSectionHashes: bookUpdate.normalizedSectionHashes
    ? [...bookUpdate.normalizedSectionHashes]
    : undefined,
});

const writeLocalBook = (book: BookMeta) => {
  const books = getBooks();
  books[book.id] = book;
  updateBooksCache(books);
  console.debug("wrote local book", summarizeBookForDebug(book));
};

const remoteBookUpdateQueue = new Map<string, Promise<void>>();
const inFlightReportOpenById = new Map<string, Promise<BookMeta | undefined>>();

const markRemoteBookSync = (timestamp = Date.now()) => {
  updateMisc({ ...getMisc(), lastRemoteUpdateTimestamp: timestamp });
};

const sendRemoteBookUpdate = async (
  remoteUpdatePayload: BookRemoteUpdate,
): Promise<void> => {
  const response = await fetch("/api/update_book", {
    credentials: "include",
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(remoteUpdatePayload),
  });

  if (response.status === 401) {
    clearCurrentUser();
    // deleteCookie();
    fetchUser(true);
    return;
  }

  const data = await response.json();
  if (!data.error) {
    console.debug("received data on updateBook", data);
  } else {
    console.error("data error on updateBook", data);
  }
};

const queueRemoteBookUpdate = async (
  remoteUpdatePayload: BookRemoteUpdate,
): Promise<void> => {
  const previousUpdate = remoteBookUpdateQueue.get(remoteUpdatePayload.id);
  const queuedUpdate = (async () => {
    if (previousUpdate) {
      await previousUpdate.catch(() => undefined);
    }
    await sendRemoteBookUpdate(remoteUpdatePayload);
  })();

  remoteBookUpdateQueue.set(remoteUpdatePayload.id, queuedUpdate);
  try {
    await queuedUpdate;
  } finally {
    if (remoteBookUpdateQueue.get(remoteUpdatePayload.id) === queuedUpdate) {
      remoteBookUpdateQueue.delete(remoteUpdatePayload.id);
    }
  }
};

export const updateBook = async (
  bookUpdate: BookMeta,
  important: boolean = false,
  remoteBookUpdate?: BookRemoteUpdate,
): Promise<BookMeta | undefined> => {
  const isOnline = useOnlineStore.getState().isOnline;
  const books = getBooks();
  const localBook = books[bookUpdate.id] || defaultBookMeta;
  const parentIdWasProvided = Object.prototype.hasOwnProperty.call(bookUpdate, "parentId");
  const parentUpdatedAtWasProvided = Object.prototype.hasOwnProperty.call(bookUpdate, "parentUpdatedAt");
  const parentIdChanged = parentIdWasProvided && localBook.parentId !== bookUpdate.parentId;
  const parentUpdatedAtChanged =
    parentUpdatedAtWasProvided && localBook.parentUpdatedAt !== bookUpdate.parentUpdatedAt;
  const folderPlacementChanged = parentIdChanged || parentUpdatedAtChanged;
  const updatedBook = mergeBookMeta(localBook, bookUpdate);
  if (
    parentIdChanged &&
    (!parentUpdatedAtWasProvided ||
      !bookUpdate.parentUpdatedAt ||
      bookUpdate.parentUpdatedAt === localBook.parentUpdatedAt)
  ) {
    updatedBook.parentUpdatedAt = Date.now();
  }
  const remoteUpdatePayload = remoteBookUpdate
    ? cloneRemoteBookUpdate(remoteBookUpdate)
    : updatedBook;
  const currentTime = Date.now();
  const lastRemoteUpdateTimestamp = getMisc()?.lastRemoteUpdateTimestamp || 0;
  const secondsSinceLastRemoteUpdate = Math.ceil(
    (currentTime - lastRemoteUpdateTimestamp) / 1000,
  );

  if (secondsSinceLastRemoteUpdate > 60 || important) {
    polishBook(updatedBook);
  }
  books[updatedBook.id] = updatedBook;
  updateBooksCache(books);

  if (secondsSinceLastRemoteUpdate < 60 && !important && !folderPlacementChanged) {
    return updatedBook;
  }
  console.debug(
    "updating book because ",
    important ? "important" : folderPlacementChanged ? "folderPlacementChanged" : "secondsSinceLastRemoteUpdate",
  );

  if (Object.keys(updatedBook).length > 0 && getCurrentUser()) {
    console.log(
      "/api/update_book with: ",
      summarizeBookForDebug(remoteUpdatePayload),
    );
    updateMisc({ ...getMisc(), lastRemoteUpdateTimestamp: currentTime });
    if (!isOnline) {
      return updatedBook;
    }

    try {
      await queueRemoteBookUpdate(remoteUpdatePayload);
    } catch (error) {
      console.error(
        "failed on updateBook",
        summarizeBookForDebug(updatedBook),
        error,
      );
    }
    return updatedBook;
  }
  return;
};

const readingPositionKeys = ["progress", "location", "locationUpdatedAt"] as const;

const readingPositionTimestamp = (settings?: Partial<BookSettings>): number =>
  getReadingPositionTimestamp(settings);

const hasReadingPositionValue = (settings?: Partial<BookSettings>): boolean =>
  readingPositionKeys.some((key) => settings?.[key] !== undefined);

const mergeBookSettings = (
  oldSettings: Partial<BookSettings> = {},
  newSettings: Partial<BookSettings> = {},
  options: MergeBookMetaOptions = {},
): BookSettings => {
  const existingSettings = oldSettings || {};
  const incomingSettings = newSettings || {};
  const mergedSettings: BookSettings = {
    ...existingSettings,
    ...incomingSettings,
    updates: dedupe([
      ...(existingSettings.updates || []),
      ...(incomingSettings.updates || []),
    ]),
  };
  const oldPositionTimestamp = readingPositionTimestamp(existingSettings);
  const newPositionTimestamp = readingPositionTimestamp(incomingSettings);
  const positionSource =
    oldPositionTimestamp > newPositionTimestamp ||
    (options.preferExistingReadingPositionOnTie &&
      oldPositionTimestamp === newPositionTimestamp &&
      hasReadingPositionValue(existingSettings)) ||
    (oldPositionTimestamp === newPositionTimestamp &&
      !options.preferExistingReadingPositionOnTie &&
      !hasReadingPositionValue(incomingSettings))
      ? existingSettings
      : incomingSettings;

  for (const key of readingPositionKeys) {
    if (positionSource[key] !== undefined) {
      (mergedSettings as Record<string, unknown>)[key] = positionSource[key];
    } else {
      delete (mergedSettings as Record<string, unknown>)[key];
    }
  }

  if (
    oldPositionTimestamp > newPositionTimestamp &&
    (existingSettings.progress !== incomingSettings.progress ||
      existingSettings.location !== incomingSettings.location)
  ) {
    logger.debug("mergeBookSettings preserved newer reading position", {
      oldPositionTimestamp,
      newPositionTimestamp,
      oldProgress: existingSettings.progress,
      newProgress: incomingSettings.progress,
      oldLocation: existingSettings.location || null,
      newLocation: incomingSettings.location || null,
    });
  }

  return mergedSettings;
};

const reportOpenRequest = async (id: string): Promise<BookMeta | undefined> => {
  const isOnline = useOnlineStore.getState().isOnline;
  const user = fetchUser();
  if (!user) {
    console.log("No user found");
    return Promise.reject("No user found"); // or return Promise.resolve(null); if you prefer
  }

  const localBook = getBook(id);
  if (!localBook) {
    console.error("No book found with id", id);
    return Promise.reject("No book found");
    // return Promise.reject("No book found"); // or return Promise.resolve(null); if you prefer
  }

  if (!isOnline) {
    return localBook;
  }
  const reportOpenStartedAt = Date.now();
  logger.debug(
    "reportOpen: requesting remote book open",
    summarizeBookForDebug(localBook),
  );
  try {
    const response = await fetch("/api/open_book", {
      credentials: "include", // Necessary to include cookies with the request
      method: "POST",
      headers: {
        "Content-Type": "application/json", // Ensure you set the content type header
      },
      body: JSON.stringify(localBook),
    });
    let data = await response.json();
    if (!data.error) {
      const remoteBook: BookMeta = data;
      logger.debug(
        "reportOpen: received remote book",
        summarizeBookForDebug(remoteBook),
      );

      // Merge against the freshest local book, not the stale snapshot captured
      // before the network request, so opening a book cannot clobber a newer
      // location written while the request was in flight.
      const freshestLocalBook = getBook(id) || localBook;
      const mergedBook = mergeBookMeta(remoteBook, freshestLocalBook, {
        preferExistingReadingPositionOnTie: true,
      });
      logger.debug(
        "reportOpen: merged local and remote book",
        {
          ...summarizeBookForDebug(mergedBook),
          elapsedMs: Date.now() - reportOpenStartedAt,
        },
      );
      writeLocalBook(mergedBook);
      markRemoteBookSync();
      return mergedBook;
    } else {
      console.warn("data error on reportOpen", data);
      return;
      // return Promise.reject(data.error); // Reject the promise if there's an error
    }
  } catch (error) {
    console.error("Fetch error on reportOpen", error);
    // fetchUser(true);
    return;
    // return Promise.reject(error); // Reject the promise if the fetch fails
  }
};

const reportOpenBase = async (id: string): Promise<BookMeta | undefined> => {
  const inFlightReportOpen = inFlightReportOpenById.get(id);
  if (inFlightReportOpen) {
    logger.debug("reportOpen: reusing in-flight open request", { id });
    return inFlightReportOpen;
  }

  const reportOpenPromise = reportOpenRequest(id);
  inFlightReportOpenById.set(id, reportOpenPromise);
  try {
    return await reportOpenPromise;
  } finally {
    if (inFlightReportOpenById.get(id) === reportOpenPromise) {
      inFlightReportOpenById.delete(id);
    }
  }
};

export const upgrade = async () => {
  if (inFlightUpgrade) {
    return inFlightUpgrade;
  }

  inFlightUpgrade = (async () => {
    const isOnline = useOnlineStore.getState().isOnline;
    if (!isOnline) {
      return;
    }
    const updateVersion = (storeVersion: number): number => {
      updateMisc({ ...getMisc(), storeVersion: storeVersion });
      console.log("set storeVersion to", storeVersion);
      return storeVersion;
    };

    await loadFromLocalForage();

    let version =
      getMisc()?.storeVersion ||
      parseInt(localStorage.getItem("version") || "24", 10);
    const books = getBooks();
    if (!books) {
      return version;
    }

    try {
      console.debug("store version is currently ", version);
      if (version < 25) {
        console.log("upgrading storage to localforage");
        try {
          // Migrate data from localStorage to localforage
          const booksJson = localStorage.getItem(localStorageBooksName);
          if (booksJson) {
            const books = JSON.parse(booksJson);
            updateBooksCache(books);
          }

          const notesStatusJson = localStorage.getItem(
            localStorageNotesStatusName,
          );
          if (notesStatusJson) {
            const notesStatus = JSON.parse(notesStatusJson);
            updateNotesStatus(notesStatus);
          }

          const following = JSON.parse(
            localStorage.getItem("following") || "[]",
          ) as Person[];
          updateFollowing(following);

          const currentUser = JSON.parse(
            localStorage.getItem("currentUser") || "{}",
          ) as UserInfo;
          updateCurrentUser(currentUser);

          const currentVersion =
            localStorage.getItem("currentVersion") || "4.4.2";
          const storeVersion = localStorage.getItem("storeVersion") || 24;
          const lastRemoteUpdateTimestamp = parseInt(
            localStorage.getItem("lastRemoteUpdateTimestamp") || "0",
            10,
          );
          const lastVersionCheck = parseInt(
            localStorage.getItem("lastVersionCheck") || "0",
            10,
          );

          const misc = {
            version: currentVersion,
            lastVersionCheck: lastVersionCheck,
            lastRemoteUpdateTimestamp: lastRemoteUpdateTimestamp,
            storeVersion: storeVersion,
          } as Misc;

          updateMisc(misc);

          if (getMisc()?.version && Object.keys(getBooks()).length > 0) {
            localStorage.removeItem(localStorageNotesStatusName);
            localStorage.removeItem(localStorageBooksName);
            localStorage.removeItem("version");
            localStorage.removeItem("currentVersion");
            localStorage.removeItem("lastVersionCheck");
            localStorage.removeItem("lastRemoteUpdateTimestamp");
            localStorage.removeItem("following");
            localStorage.removeItem("currentUser");
            console.log("Successfully migrated to localforage");
            version = updateVersion(25);
          } else {
            console.error("not ideal!");
            version = updateVersion(25);
          }
        } catch (error) {
          console.error("Error migrating to localforage:", error);
          throw error;
        }
      }
      // if (version < 26) {
      //   console.log('migrating to groups');
      //   try {
      //     // Migrate data from localStorage to localforage
      //     const user = getCurrentUser();
      //     if (!user) {
      //       console.error('No user found in migrateShelvesToGroups');
      //       return;
      //     }
      //     const groups = migrateShelvesToGroups(t);
      //     const activeGroupId = getRootGroup(groups)?.id;
      //     updateCurrentUser({ ...user, prefs: { ...user.prefs, groups, activeGroupId } });
      //     version = updateVersion(26);
      //   } catch (error) {
      //     console.error('Error migrating to groups:', error);
      //     throw error;
      //   }
      // }
      if (version < 26) {
        const currentUser = getCurrentUser();
        const { books: booksWithIdentity, changed } = await backfillBookIdentityCollection(
          getBooks(),
          currentUser?.email,
        );
        if (changed) {
          updateBooksCache(booksWithIdentity);
        }
        version = updateVersion(26);
      }

      if (version < 27) {
        const currentUser = getCurrentUser();
        if (!currentUser?.email) {
          console.warn("upgrade: waiting for current user before backfilling comparison metadata");
          return version;
        }

        const currentBooks = Object.values(getBooks());
        let identityBackfilled = 0;
        let comparisonBackfilled = 0;
        let skippedComparison = 0;

        for (const originalBook of currentBooks) {
          const identifiedBook = await ensureBookIdentityFields(
            originalBook,
            currentUser.email,
            {
              preserveWorkId: Boolean(originalBook.workId),
              preserveEditionId: Boolean(originalBook.editionId),
            },
          );
          const workingBook = hasDifferentIdentityFields(originalBook, identifiedBook)
            ? identifiedBook
            : originalBook;

          if (workingBook !== originalBook) {
            await updateBook(workingBook, true);
            identityBackfilled += 1;
          }

          const expectedSectionCount = workingBook.chapterHashes?.length
            ? workingBook.chapterHashes.length
            : undefined;
          if (hasCurrentBookComparisonFields(workingBook, expectedSectionCount)) {
            continue;
          }

          const parsedBook = await loadBook(workingBook.id);
          if (!parsedBook) {
            skippedComparison += 1;
            console.warn("upgrade: missing local EPUB for comparison backfill", {
              bookId: workingBook.id,
              title: workingBook.title,
            });
            continue;
          }

          await getChapters(stripDerivedTextMetadata(workingBook), parsedBook);
          comparisonBackfilled += 1;
        }

        console.info("upgrade: backfilled local book identity/comparison metadata", {
          scanned: currentBooks.length,
          identityBackfilled,
          comparisonBackfilled,
          skippedComparison,
        });
        version = updateVersion(27);
      }
    } catch (error) {
      console.error("Failed to update books at version", version);
      throw error;
    } finally {
      return version;
    }
  })();

  try {
    return await inFlightUpgrade;
  } finally {
    inFlightUpgrade = null;
  }
};

const fetchFollowingBase = async () => {
  const isOnline = useOnlineStore.getState().isOnline;
  const user = fetchUser();
  if (!user) {
    return [];
  }
  if (!isOnline) {
    return [];
  }
  console.log("fetching following...");
  try {
    const response = await fetch("/api/get_following", {
      credentials: "include",
    });

    if (response.status === 401) {
      // Clear all user data on 401 unauthorized
      clearCurrentUser();
      //   updateFollowing([]);
      //   updateNotesStatus({});
      //   updateMisc(null);
      // Force refresh user data
      //   await fetchUser(true);
      return [];
    }

    const data = await response.json();
    if (!data.error) {
      updateFollowing(data);
      return data;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Failed to fetch following", error);
    return [];
  }
};

export const getDisplayName = async (email: string) => {
  let following: Person[] = getFollowing() || [];
  // Attempt to find the user in the already fetched list
  let user = following.find((e) => e.email === email);

  if (!user) {
    // Fetch following if the user wasn't found or the list was empty
    following = await fetchFollowing();
    user = following.find((e) => e.email === email);
  }

  return user ? user.given_name : "Anonymous";
};

export const getPerson = (email: string): Person | null => {
  if (email === "ai") {
    return aiUser;
  }
  // Attempt to find the user in the already fetched list
  let user = getFollowing()?.find((e) => e.email === email);

  // if (!user) {
  //   // Fetch following if the user wasn't found or the list was empty
  //   following = await fetchFollowing();
  //   user = following.find((e) => e.email === email);
  // }

  if (user) {
    return user;
  } else {
    return null;
  }
};

export const cleanUpNotes = (book: BookMeta): boolean => {
  // Keep tombstones in storage so stale remote entries cannot resurrect notes.
  book.notes = mergeNotes({}, book.notes);

  let changed = false;
  Object.values(book.notes).forEach((note: Note) => {
    if (!note.timestamp) {
      note.timestamp = new Date().toISOString();
      changed = true;
    }
    if (!note.id) {
      note.id = crypto.randomUUID();
      changed = true;
    }
    if (
      note.comment &&
      (!note.comments || Object.keys(note.comments).length === 0)
    ) {
      const id = crypto.randomUUID();
      note.comments = {
        [id]: {
          text: note.comment,
          owner: note.owner,
          timestamp: note.timestamp || new Date().toISOString(),
          id: id,
          reactions: {},
        },
      };
      changed = true;
    }
  });
  return changed;
};

export const getBook = (id: string): BookMeta | undefined => {
  const books = getBooks();
  let book = books[id];
  if (!book) {
    return;
  }
  if (!book.notes) {
    book.notes = {};
  } else if (cleanUpNotes(book)) {
    console.warn("cleanUpNotes changed book ", book);
    updateBook(book);
  }
  return book;
};

export const getUserId = (): string => {
  const user = getCurrentUser();
  return user?.email || "";
};

let fetchingUser = false;
export const fetchUser = async (
  force: boolean = false,
): Promise<UserInfo | null | undefined> => {
  const user = getCurrentUser();
  if (!force && user) {
    return user;
  }
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return user ?? undefined;
  }

  if (fetchingUser) {
    return;
  }
  fetchingUser = true;

  console.log("fetching current user from server...");
  // Fetch the current user's information from the backend
  try {
    const response = await fetch("/api/current_user", {
      credentials: "include", // Necessary to include cookies with the request
    });
    const data = await response.json();
    if (!data.error && data.email) {
      console.log("fetched user from server: ", data);
      updateCurrentUser(data);
      fetchingUser = false;
      return data;
    } else {
      //   clearCurrentUser();
      // deleteCookie();
      console.error("data error fetching user: ", data);
      // window.dispatchEvent(new CustomEvent('logout'));
      fetchingUser = false;
      return null; // No user logged in, or session expired
    }
  } catch (error) {
    console.error("error fetching user: ", error);
    fetchingUser = false;
    if (force && user) {
      return user;
    }
    return undefined;
  } finally {
    fetchingUser = false;
  }
};

export const dedupe = (data: UpdateRecord[], field = 0): UpdateRecord[] => {
  const seen = new Set();
  return data.filter((item) => {
    if (seen.has(item[field])) {
      return false;
    }
    seen.add(item[field]);
    return true;
  });
};

const getUpdatesBase = async (id: string): Promise<UpdateRecord[]> => {
  const book = getBook(id);
  if (!book) {
    return [];
  }

  const user = fetchUser();
  if (!user) {
    console.log("No user found in getUpdatesBase");
    return book.settings?.updates || [];
  }

  const hash = book.hash;
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return [];
  }
  console.log("/api/get_updates", hash);
  try {
    const response = await fetch("/api/get_updates", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ hash: hash }),
    });

    let data = await response.json();
    if (!data.error) {
      //   await updateBook({ ...book, settings: { updates: data } });
      //   writeLocalBook(mergeBookMeta({ ...book, settings: { updates: data } }, book));
      return data; // Successfully return the data
    } else {
      console.error("data error: ", data);
      return []; // Return empty array if there's an error in the data
    }
  } catch (error) {
    console.error("getUpdates error: ", error);
    return []; // Return empty array on fetch error
  }
};

export const logoutUser = () => {
  // Optional: Inform your backend to invalidate the session
  clearCurrentUser();
  //   deleteCookie();
  fetch("/api/logout", {
    credentials: "include", // If you're using cookies to manage session
    // Additional fetch options as needed
  })
    .then((response) => {
      // Handle response
      if (response.ok) {
        // Clear user state/frontend session
        console.log("logged out okay");
      }
    })
    .catch((error) => {
      console.error("Logout error", error);
    });
};

export const saveLocations = async (id: string, locations: any) => {
  await saveObject(`locations-${id}`, locations);
  console.debug("saveLocations for ", id);
};

export const loadLocations = async (id: string) => {
  //   const locs = JSON.parse(localStorage.getItem("locations") || "{}");
  // if (Object.keys(locs).includes(id))
  const locs = await loadObject(`locations-${id}`);
  if (!locs) {
    console.log("no locations for ", id);
  }
  //   console.debug("loadLocations loc = ", locs);
  return locs;
};

/**
 * Updates the current user's information in local storage.
 *
 * @param {Partial<UserInfo>} user - Partial user information to update.
 * @throws {Error} Logs an error if no current user is found.
 * @see {@link mergePrefs}
 *
 * @description
 * This function retrieves the current user from local storage,
 * merges the new user information with the existing data,
 * and updates the local storage with the combined information.
 * It also merges the user preferences using the `mergePrefs` function.
 * If no current user is found, it logs an error and returns without making changes.
 */
export const updateUser = (
  user: Partial<UserInfo>,
  prefs?: Partial<Prefs>,
): UserInfo | undefined => {
  const savedUser = getCurrentUser();
  if (!savedUser) {
    console.error("No user found in updateUser");
    return;
  }
  const newPrefs = mergePrefs(mergePrefs(savedUser.prefs, user.prefs), prefs);
  const newUser = {
    ...savedUser,
    ...user,
    prefs: newPrefs,
  };
  return updateCurrentUser(newUser);
};

/**
 * Merges user preferences with new preferences and default preferences.
 *
 * @param {Partial<Prefs>} userPrefs - The existing user preferences.
 * @param {Partial<Prefs>} newPrefs - The new preferences to be merged.
 * @returns {Prefs} A new Prefs object containing the merged preferences.
 *
 * @description
 * This function combines three sources of preferences:
 * 1. Default preferences (defaultPrefs)
 * 2. Existing user preferences (userPrefs)
 * 3. New preferences to be applied (newPrefs)
 *
 * It handles special merging for the statusBarBoxes array and the favorite object.
 * For statusBarBoxes, it combines all sources and removes duplicates based on position.
 * For favorite, it performs a shallow merge of all sources.
 */
export const mergePrefs = (
  userPrefs: Partial<Prefs> = {},
  newPrefs: Partial<Prefs> = {},
): Prefs => {
  const isArray = (value: any): value is any[] => Array.isArray(value);

  return {
    ...defaultPrefs,
    ...userPrefs,
    ...newPrefs,
    statusBarBoxes: Array.from(
      [
        ...defaultStatusBarBoxes,
        ...(isArray(userPrefs?.statusBarBoxes) ? userPrefs.statusBarBoxes : []),
        ...(isArray(newPrefs?.statusBarBoxes) ? newPrefs.statusBarBoxes : []),
      ]
        .reduce((acc, item) => {
          acc.set(item.position, item);
          return acc;
        }, new Map<number, StatusBarBoxInfo>())
        .values(),
    ),
    favorite: {
      ...defaultFavorite,
      ...(userPrefs?.favorite || {}),
      ...(newPrefs?.favorite || {}),
    },
  };
};

export const getPrefs = (): Prefs => {
  //   const userJson = fetchUser();
  //   return mergePrefs(userJson?.prefs);
  //   return mergePrefs(getCurrentUser()?.prefs);
  return getCurrentUser()?.prefs || {};
};

/**
 * Pushes user preferences to the server.
 *
 * @async
 * @returns {Promise<any>} A promise that resolves when the preferences are successfully updated, or rejects with an error.
 *
 * @description
 * This function merges the provided partial preferences with the existing user preferences,
 * then sends the updated preferences to the server. It handles various error cases,
 * including no user found, server errors, and network errors.
 */
export const pushPrefs = async (): Promise<any> => {
  const user = await fetchUser();
  if (!user) {
    console.log("No user found in update_prefs");
    return Promise.reject("No user found");
  }
  //   if (!fetchedUser) {
  //     console.warn('pushPrefs called before user was fetched');
  //     return;
  //   }
  const prefs = user.prefs;
  console.debug("pushPrefs", prefs);

  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return;
  }
  try {
    const response = await fetch("/api/update_prefs", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prefs: prefs,
      }),
    });
    const data = await response.json();
    if (!data.error) {
      console.log("pushed new prefs: ", prefs);
      return;
    } else {
      console.error("data error on update_prefs", data);
      return Promise.reject(data.error);
    }
  } catch (error) {
    console.error("Fetch error on update_prefs", error);
    fetchUser(true);
    return Promise.reject(error);
  }
};

const getStreaksBase = async (
  users: string[],
): Promise<{ [id: string]: { days: number; today: boolean } } | undefined> => {
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return;
  }
  try {
    const response = await fetch("/api/get_streaks", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(users),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch streaks");
    }
    const data = await response.json();
    console.log("fetched streaks:", data);
    return data;
  } catch (error) {
    useOnlineStore.setState({ isOnline: false });
    console.error("Error fetching streaks:", error);
    throw error;
  }
};

const getBookProgressAndNotesBase = async (hashes: string[]): Promise<any> => {
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return;
  }
  const startTime = Date.now();
  try {
    const response = await fetch("/api/get_progress_and_notes", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ hashes }),
    });
    console.log(
      `getBookProgressAndNotesBase fetch took ${Date.now() - startTime}ms`,
    );

    if (!response.ok) {
      throw new Error("Failed to fetch book progress and notes");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching book progress and notes:", error);
    throw error;
  }
};

export const getBookProgressAndNotes = async (
  hashes: string[],
): Promise<any> => {
  return await getBookProgressAndNotesBase(hashes);
};

export const getBookNotes = async (hash: string): Promise<NotesGroup> => {
  const response = await fetch(`/api/notes/${encodeURIComponent(hash)}`, {
    credentials: "include",
  });
  const data = await response.json();
  if (!response.ok || data.error) {
    throw new Error(data.error || "Failed to fetch book notes");
  }
  return data.notes || {};
};

const getBookProgressBase = async (hashes: string[]): Promise<any> => {
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return;
  }
  const startTime = Date.now();
  try {
    const response = await fetch("/api/get_progress", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(hashes),
    });
    console.log(`getBookProgressBase fetch took ${Date.now() - startTime}ms`);

    if (!response.ok) {
      throw new Error("Failed to fetch book progress");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching book progress:", error);
    throw error;
  }
};

export const getBookData = async (book: BookMeta): Promise<any> => {
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    return;
  }
  try {
    const response = await fetch("/api/get_book", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(book),
    });
    if (!response.ok) {
      return {};
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching book progress:", error);
    return {};
  }
};

/**
 * Calculates the median value from an array of numbers within specified bounds.
 *
 * @param values - Array of numbers to calculate median from
 * @param midpoint - The relative position to select (0.5 = median)
 * @param lowerBound - Optional lower bound (0.0 to 1.0) to filter values
 * @param upperBound - Optional upper bound (0.0 to 1.0) to filter values
 * @returns The median value, or 0 if no values remain after filtering
 */
// const median = (values: number[], midpoint: number = 0.5, lowerBound?: number, upperBound?: number): number => {
//   if (values.length === 0) return 0;

//   // Sort the array
//   values.sort((a, b) => a - b);

//   // Calculate bounds indices
//   const start = lowerBound ? Math.floor(values.length * lowerBound) : 0;
//   const end = upperBound ? Math.ceil(values.length * upperBound) : values.length;

//   // Filter values within bounds
//   const filteredValues = values.slice(start, end);
//   if (filteredValues.length === 0) return 0;

//   // Calculate median position within filtered values
//   const mid = Math.round(filteredValues.length * midpoint);
//   return filteredValues[mid];
// };

/**
 * Calculates the mean value from an array of numbers within specified bounds.
 *
 * @param values - Array of numbers to calculate mean from
 * @param lowerBound - Optional lower bound (0.0 to 1.0) to filter values
 * @param upperBound - Optional upper bound (0.0 to 1.0) to filter values
 * @returns The mean value, or 0 if no values remain after filtering
 */
const mean = (
  values: number[],
  lowerBound?: number,
  upperBound?: number,
): number => {
  if (values.length === 0) return 0;

  // Sort the array
  values.sort((a, b) => a - b);

  // Calculate bounds indices
  const start = lowerBound ? Math.floor(values.length * lowerBound) : 0;
  const end = upperBound
    ? Math.ceil(values.length * upperBound)
    : values.length;

  // Filter values within bounds
  const filteredValues = values.slice(start, end);
  if (filteredValues.length === 0) return 0;

  // Calculate mean of filtered values
  const sum = filteredValues.reduce((acc, val) => acc + val, 0);
  return sum / filteredValues.length;
};

type ReadingInterval = {
  minutes: number;
  words: number;
  wpm: number;
};

export type FocusMetrics = {
  representativeWPM: number;
  focus: number;
};

const MIN_FOCUS_INTERVALS = 5;
const MIN_FOCUS_MINUTES = 5;
const FOCUS_CHUNK_TARGET_MINUTES = 3;
const FOCUS_MIN_PACE_RATIO = 0.7;
const MAX_VALID_READING_WPM = 800;
const FOCUS_SESSION_RESET_PROGRESS_DROP = 0.25;
const FOCUS_SESSION_RESTART_MAX_PROGRESS = 0.2;
const FOCUS_SESSION_RESTART_LOOKAHEAD_UPDATES = 8;
const FOCUS_SESSION_RESTART_CONFIRMATION_UPDATES = 3;
const FOCUS_SESSION_RESTART_CONFIRMATION_MAX_PROGRESS = 0.35;

const getReadingIntervals = (
  updates: UpdateRecord[] | undefined,
  wordCount: number,
  lowerBoundSeconds = 10,
  upperBoundSeconds = 5 * 60,
  upperBoundPropDiff = 0.05,
): ReadingInterval[] => {
  if (!updates || updates.length < 2 || wordCount <= 0) {
    return [];
  }

  const sortedUpdates = [...updates].sort((a, b) => a[0] - b[0]);
  const intervals: ReadingInterval[] = [];

  for (let i = 1; i < sortedUpdates.length; i++) {
    const [prevTime, prevProgress] = sortedUpdates[i - 1];
    const [currentTime, currentProgress] = sortedUpdates[i];
    const timeDiffSeconds = (currentTime - prevTime) / 1000;
    const progressDiff = currentProgress - prevProgress;

    if (
      timeDiffSeconds <= lowerBoundSeconds ||
      timeDiffSeconds >= upperBoundSeconds ||
      progressDiff <= 0 ||
      progressDiff >= upperBoundPropDiff
    ) {
      continue;
    }

    const minutes = timeDiffSeconds / 60;
    const words = wordCount * progressDiff;
    const wpm = words / minutes;

    if (!Number.isFinite(wpm) || wpm <= 0 || wpm > MAX_VALID_READING_WPM) {
      continue;
    }

    intervals.push({
      minutes,
      words,
      wpm,
    });
  }

  return intervals;
};

const getTotalIntervalMinutes = (intervals: ReadingInterval[]): number => {
  return intervals.reduce((acc, interval) => acc + interval.minutes, 0);
};

const getReadingSessions = (
  updates: UpdateRecord[] | undefined,
  resetProgressDrop: number = FOCUS_SESSION_RESET_PROGRESS_DROP,
  restartMaxProgress: number = FOCUS_SESSION_RESTART_MAX_PROGRESS,
  restartLookaheadUpdates: number = FOCUS_SESSION_RESTART_LOOKAHEAD_UPDATES,
  restartConfirmationUpdates: number = FOCUS_SESSION_RESTART_CONFIRMATION_UPDATES,
  restartConfirmationMaxProgress: number = FOCUS_SESSION_RESTART_CONFIRMATION_MAX_PROGRESS,
): UpdateRecord[][] => {
  if (!updates || updates.length < 2) {
    return [];
  }

  const sortedUpdates = [...updates].sort((a, b) => a[0] - b[0]);
  const sessions: UpdateRecord[][] = [[sortedUpdates[0]]];

  for (let i = 1; i < sortedUpdates.length; i++) {
    const previousUpdate = sortedUpdates[i - 1];
    const currentUpdate = sortedUpdates[i];
    const previousProgress = previousUpdate[1];
    const currentProgress = currentUpdate[1];
    const progressReset =
      previousProgress - currentProgress >= resetProgressDrop &&
      currentProgress <= restartMaxProgress;

    let confirmedRestart = false;
    if (progressReset) {
      const futureProgressValues = sortedUpdates
        .slice(i, Math.min(sortedUpdates.length, i + restartLookaheadUpdates))
        .map(([_time, progress]) => progress);
      confirmedRestart =
        futureProgressValues.length >= restartConfirmationUpdates &&
        Math.max(...futureProgressValues) <= restartConfirmationMaxProgress;
    }

    if (confirmedRestart) {
      sessions.push([currentUpdate]);
      continue;
    }

    sessions[sessions.length - 1].push(currentUpdate);
  }

  return sessions.filter((session) => session.length > 1);
};

const getLatestReadingUpdates = (
  updates: UpdateRecord[] | undefined,
): UpdateRecord[] | undefined => {
  if (!updates || updates.length < 2) {
    return updates;
  }

  const sessions = getReadingSessions(updates);
  if (sessions.length === 0) {
    return [...updates].sort((a, b) => a[0] - b[0]);
  }

  return sessions[sessions.length - 1];
};

const getSmoothedReadingIntervals = (
  intervals: ReadingInterval[],
  targetMinutes: number = FOCUS_CHUNK_TARGET_MINUTES,
): ReadingInterval[] => {
  if (intervals.length === 0) {
    return [];
  }

  const smoothedIntervals: ReadingInterval[] = [];
  let accumulatedMinutes = 0;
  let accumulatedWords = 0;

  for (const interval of intervals) {
    accumulatedMinutes += interval.minutes;
    accumulatedWords += interval.words;

    if (accumulatedMinutes >= targetMinutes) {
      smoothedIntervals.push({
        minutes: accumulatedMinutes,
        words: accumulatedWords,
        wpm: accumulatedWords / accumulatedMinutes,
      });
      accumulatedMinutes = 0;
      accumulatedWords = 0;
    }
  }

  if (accumulatedMinutes > 0) {
    smoothedIntervals.push({
      minutes: accumulatedMinutes,
      words: accumulatedWords,
      wpm: accumulatedWords / accumulatedMinutes,
    });
  }

  return smoothedIntervals;
};

const getRepresentativeWPMFromIntervals = (
  intervals: ReadingInterval[],
): number => {
  const totalMinutes = getTotalIntervalMinutes(intervals);
  if (totalMinutes <= 0) {
    return 0;
  }

  const totalWords = intervals.reduce(
    (acc, interval) => acc + interval.words,
    0,
  );
  return totalWords / totalMinutes;
};

export const getBookFocusMetrics = (book: BookMeta): FocusMetrics | null => {
  if (
    !book.wordCount ||
    !book.settings.updates ||
    book.settings.updates.length < MIN_FOCUS_INTERVALS + 1
  ) {
    return null;
  }

  const latestReadingUpdates = getLatestReadingUpdates(book.settings.updates);
  const intervals = getReadingIntervals(latestReadingUpdates, book.wordCount);
  const totalMinutes = getTotalIntervalMinutes(intervals);
  if (
    intervals.length < MIN_FOCUS_INTERVALS ||
    totalMinutes < MIN_FOCUS_MINUTES
  ) {
    return null;
  }

  const smoothedIntervals = getSmoothedReadingIntervals(intervals);
  const smoothedMinutes = getTotalIntervalMinutes(smoothedIntervals);
  if (smoothedIntervals.length === 0 || smoothedMinutes <= 0) {
    return null;
  }

  const representativeWPM =
    getRepresentativeWPMFromIntervals(smoothedIntervals);
  if (!Number.isFinite(representativeWPM) || representativeWPM <= 0) {
    return null;
  }

  const lowerBound = representativeWPM * FOCUS_MIN_PACE_RATIO;
  const focusedMinutes = smoothedIntervals.reduce((acc, interval) => {
    return acc + (interval.wpm >= lowerBound ? interval.minutes : 0);
  }, 0);

  return {
    representativeWPM,
    focus: focusedMinutes / smoothedMinutes,
  };
};

const convertBPStoWPM = (
  booksPerSecond: number[],
  wordCount: number,
): number => {
  //   const wordsPerMinute = wordCount * median(booksPerSecond, 0.5) * 60;
  const wordsPerMinute = wordCount * mean(booksPerSecond, 0.25, 0.75) * 60;
  return Math.round(wordsPerMinute);
};

const calculateBPS = (
  data: UpdateRecord[],
  wordCount: number,
  lowerBoundTimestamp = 0,
  lowerBoundSeconds = 10,
  upperBoundSeconds = 5 * 60,
  upperBoundPropDiff = 0.05,
): number[] => {
  let recentData = data;
  if (lowerBoundTimestamp) {
    recentData = data.filter(([time]) => time >= lowerBoundTimestamp);
  }
  if (recentData.length == 0) {
    return [];
  }

  recentData.sort((a, b) => a[0] - b[0]);

  const booksPerSecond: number[] = [];
  const seenProps = new Set<number>();

  for (let i = 1; i < recentData.length; i++) {
    const [prevTime, prevProp] = recentData[i - 1];
    while (i < recentData.length - 1 && seenProps.has(recentData[i][1])) {
      i++;
    }
    const [currentTime, currentProp] = recentData[i];

    const timeDiff = (currentTime - prevTime) / 1000;
    const propDiff = currentProp - prevProp;
    const wordsPerMinute =
      wordCount > 0 ? (wordCount * propDiff * 60) / timeDiff : 0;
    if (
      lowerBoundSeconds < timeDiff &&
      timeDiff < upperBoundSeconds &&
      0 < propDiff &&
      propDiff < upperBoundPropDiff &&
      0 < wordsPerMinute &&
      wordsPerMinute <= MAX_VALID_READING_WPM
    ) {
      seenProps.add(recentData[i][1]);
      booksPerSecond.push(propDiff / timeDiff);
    }
  }

  return booksPerSecond;
};

export const calculateWPM = (
  data: UpdateRecord[],
  wordCount: number,
  hoursAgo?: number,
  _recentDataPoints: number = 10,
  savedTotalWPM?: number,
): number | undefined => {
  if (savedTotalWPM && !hoursAgo) {
    return savedTotalWPM;
  }

  if (data.length < 2 || wordCount <= 0) {
    return undefined;
  }

  const totalWPM = savedTotalWPM ?? (() => {
    const totalBooksPerSecond = calculateBPS(data, wordCount);
    return totalBooksPerSecond.length > 0
      ? convertBPStoWPM(totalBooksPerSecond, wordCount)
      : undefined;
  })();

  if (!hoursAgo) {
    return totalWPM;
  }

  const lowerBoundTimestamp = Date.now() - 1000 * 60 * 60 * hoursAgo;
  const recentBooksPerSecond = calculateBPS(
    data,
    wordCount,
    lowerBoundTimestamp,
  );

  if (recentBooksPerSecond.length === 0) {
    console.debug("[stats] no recent WPM intervals matched the requested window", {
      hoursAgo,
      updateCount: data.length,
      wordCount,
      totalWPM,
    });
    return undefined;
  }

  const recentWPM = convertBPStoWPM(recentBooksPerSecond, wordCount);
  console.debug("[stats] calculated recent WPM window", {
    hoursAgo,
    recentIntervalCount: recentBooksPerSecond.length,
    recentWPM,
    totalWPM,
  });
  return recentWPM;
};

export const getTotalWPM = async (
  book: BookMeta,
  lazy = false,
): Promise<number | undefined> => {
  if (
    book.wpm !== undefined &&
    book.settings.updates &&
    book.settings.updates.length > 1 &&
    (lazy ||
      (book.wpmTimestamp &&
        book.settings.updates[book.settings.updates.length - 1][0] -
          book.wpmTimestamp <
          MIN_UPDATE_INTERVAL))
  ) {
    return book.wpm;
  } else if (
    book.wordCount &&
    book.settings.updates &&
    book.settings.updates.length > 1
  ) {
    const wpm = calculateWPM(book.settings.updates, book.wordCount, 0) ?? 0;
    // updateBook({ ...book, wpm, wpmTimestamp: Date.now() });
    return wpm;
  } else {
    return undefined;
  }
};

const countWords = (text: string): number => {
  return Math.ceil(text.length / 5.5);
};

const wordCountTasks = new WeakMap<AnyBook, Promise<[number, number[]]>>();
const chapterTasks = new WeakMap<AnyBook, Promise<ChaptersInfo>>();
const yieldToReader = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

export const getTotalWordCount = (
  book: AnyBook,
): Promise<[number, number[]]> => {
  const existing = wordCountTasks.get(book);
  if (existing) return existing;
  const task = countBookWords(book);
  wordCountTasks.set(book, task);
  return task;
};

const countBookWords = async (
  book: AnyBook,
): Promise<[number, number[]]> => {
  let totalWords = 0;
  let sectionsCompleted = 0;
  const startedAt = performance.now();
  try {
    await book.ready;
    if (book.destroyed) return [0, []];
    console.debug("[reader-indexing] word count started", {
      sectionCount: book.packaging.spine.length,
    });
    const chapterCountsWithIndex: [number, number][] = [];
    for (const entry of book.packaging.spine as any[]) {
      // Archive reads can resolve without giving input or painting a turn.
      await yieldToReader();
      if (book.destroyed) break;
      const section: any = await book.load(entry.href);
      if (book.destroyed) break;
      if (section) {
        const text = section.body.textContent || "";
        const amount = countWords(text);

        chapterCountsWithIndex.push([entry.index, amount]);
        totalWords += amount;
      }
      sectionsCompleted += 1;
    }
    if (book.destroyed) {
      console.debug("[reader-indexing] word count cancelled for closed book", {
        sectionsCompleted,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      return [0, []];
    }
    // Sort the chapter counts based on the index and extract only the counts
    const chapterCounts = chapterCountsWithIndex
      .sort((a, b) => a[0] - b[0])
      .map(([_, count]) => count);
    console.debug("[reader-indexing] word count finished", {
      totalWords: Math.round(totalWords),
      sectionsCompleted,
      elapsedMs: Math.round(performance.now() - startedAt),
    });
    return [Math.round(totalWords), chapterCounts];
  } catch (error) {
    if (book.destroyed) return [0, []];
    wordCountTasks.delete(book);
    console.error("Error processing the ePub:", error);
    return [0, []];
  }
};

/**
 * Retrieves the anchor mappings for a given book.
 *
 * @param book - The parsed book instance.
 * @returns The anchor mappings or undefined if not found.
 */
export const getAnchorMappings = async (
  book: AnyBook,
): Promise<
  { [href: string]: { epubcfi: string; location: number } } | undefined
> => {
  const anchorsCacheKey = `anchors-${book.key()}`;
  return await loadObject(anchorsCacheKey);
};

/**
 * Loads a ParsedBook given its BookMeta ID.
 *
 * @param bookId - The unique identifier of the book to load.
 * @returns A promise that resolves to the loaded ParsedBook, or undefined if the book or file does not exist.
 *
 * @example
 * ```typescript
 * const book = await loadBook('book-id-123');
 * if (book) {
 *   // Use the book object
 * }
 * ```
 */
export const loadBook = async (
  bookId: string,
): Promise<AnyBook | undefined> => {
  console.debug("loading book with id: ", bookId);
  try {
    // Retrieve the BookMeta object for the given bookId
    const bookMeta = getBook(bookId);
    if (!bookMeta) {
      console.error(`BookMeta not found for ID: ${bookId}`);
      return undefined;
    }

    // Load the corresponding EPUB file from IndexedDB
    const fileBlob = await loadFile(bookId);
    if (!fileBlob) {
      console.error(`File not found for BookMeta ID: ${bookId}`);
      return undefined;
    }

    // Convert the Blob to an ArrayBuffer
    const arrayBuffer = await fileBlob.arrayBuffer();

    // Initialize with our own parser (no epub.js dependency)
    const book = createBook(arrayBuffer);

    // Wait until the book is fully loaded
    await book.ready;

    return book;
  } catch (error) {
    console.error(`Error loading book with ID ${bookId}:`, error);
    return undefined;
  }
};

/**
 * Retrieves the list of chapters and anchor locations for a given book.
 * Utilizes caching with IndexedDB to store and retrieve chapters and anchor locations.
 *
 * @param meta - The metadata of the book.
 * @param [withBook] - An optional ParsedBook instance to use for loading the book.
 * @returns An object containing the chapters, locations, and anchors, or null if not found.
 */
export const getChapters = (
  meta: BookMeta,
  withBook?: AnyBook,
): Promise<ChaptersInfo> => {
  const existing = withBook && chapterTasks.get(withBook);
  if (existing) return existing;
  const task = indexBookChapters(meta, withBook);
  if (withBook) {
    chapterTasks.set(withBook, task);
    const clear = () => chapterTasks.delete(withBook);
    void task.then(clear, clear);
  }
  return task;
};

const indexBookChapters = async (
  meta: BookMeta,
  withBook?: AnyBook,
): Promise<ChaptersInfo> => {
  const empty: ChaptersInfo = { sections: [], locations: [], anchors: {} };
  if (withBook?.destroyed) return empty;
  const startedAt = performance.now();
  const chaptersCacheKey = `chapters-${meta.id}`;
  const anchorsCacheKey = `anchors-${meta.id}`;
  const chapterLocsCacheKey = `chapterLocs-${meta.id}`;

  // Check if chapters are already cached
  const cachedChapters =
    await chaptersStore.getItem<RichSection[]>(chaptersCacheKey);
  const cachedAnchors =
    await anchorsStore.getItem<AnchorLocations>(anchorsCacheKey);
  const cachedChapterLocs =
    await chapterLocsStore.getItem<RichLocation[]>(chapterLocsCacheKey);
  if (withBook?.destroyed) return empty;
  const cachedCss = await getCachedCss(meta.id, withBook);
  if (withBook?.destroyed) return empty;
  const cachedHasPublisherFontFamily = cachedCss.some(cssHasPublisherFontFamily);

  const ensureComparisonFields = async (bookMeta: BookMeta, sections: RichSection[]) => {
    if (hasCurrentBookComparisonFields(bookMeta, sections.length)) {
      return { nextMeta: bookMeta, changed: false };
    }

    const comparisonFields = await buildBookComparisonFieldsFromSections(sections);
    return {
      nextMeta: {
        ...bookMeta,
        ...comparisonFields,
      },
      changed: true,
    };
  };

  if (
    cachedChapters &&
    cachedAnchors &&
    cachedChapterLocs &&
    cachedCss &&
    cachedChapters[0].fontSize &&
    (meta.chapterHashes || []).length === cachedChapters.length
  ) {
    if (meta.hasPublisherFontFamily !== cachedHasPublisherFontFamily) {
      console.debug("[publisher-font] updating cached book font metadata", {
        bookId: meta.id,
        title: meta.title,
        hasPublisherFontFamily: cachedHasPublisherFontFamily,
      });
      const { nextMeta, changed } = await ensureComparisonFields(
        {
          ...meta,
          hasPublisherFontFamily: cachedHasPublisherFontFamily,
        },
        cachedChapters,
      );
      await updateBook(nextMeta, true);
      if (changed) {
        console.debug("getChapters: backfilled comparison fields from cache", {
          bookId: meta.id,
          title: meta.title,
          comparison: summarizeBookComparisonForDebug(nextMeta),
        });
      }
    } else {
      const { nextMeta, changed } = await ensureComparisonFields(
        meta,
        cachedChapters,
      );
      if (changed) {
        console.debug("getChapters: backfilled comparison fields from cached chapters", {
          bookId: meta.id,
          title: meta.title,
          comparison: summarizeBookComparisonForDebug(nextMeta),
        });
        await updateBook(nextMeta, true);
      }
    }
    console.debug(
      "Chapters, anchors, and chapter locations loaded from localForage cache.",
    );
    return {
      sections: cachedChapters,
      locations: cachedChapterLocs,
      anchors: cachedAnchors,
    };
  }

  const chapters: RichSection[] = [];
  const anchors: AnchorLocations = {};
  const chapterLocations: RichLocation[] = [];

  const book = withBook || (await loadBook(meta.id));
  if (!book) {
    console.error("Book not found for ID:", meta.id);
    return { sections: [], locations: [], anchors: {} };
  }
  try {
    await book.ready;
    if (book.destroyed) return empty;
    console.debug("[reader-indexing] chapter scan started", {
      bookId: meta.id,
      sectionCount: book.packaging.spine.length,
    });
    let totalChars = 0;
    const css = cachedCss.join("\n\n").trim();
    const hasPublisherFontFamily = cachedHasPublisherFontFamily;

    for (const entry of book.packaging.spine as any[]) {
      await yieldToReader();
      if (book.destroyed) return empty;
      const sectionDoc = (await book.load(entry.href)) as Document;
      if (book.destroyed) return empty;
      const base = entry.cfiBase;

      if (sectionDoc) {
        const firstElementChild = sectionDoc.firstElementChild;
        if (firstElementChild) {
          const epubcfi = new EpubCFI(firstElementChild, base);
          const location = Math.floor(totalChars / locationSize);
          chapterLocations.push({ epubcfi: epubcfi.toString(), location });
        }

        let chapterTextLength = 0;

        const processNode = (node: Node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            chapterTextLength += (node.textContent || "").length;
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            if (element.id) {
              const href = `${entry.href.split("/").slice(-1)[0]}#${element.id}`;
              const epubcfi = new EpubCFI(element, base);
              const location = Math.floor(
                (totalChars + chapterTextLength) / locationSize,
              );
              anchors[href] = { epubcfi: epubcfi.toString(), location };
            }

            for (const childNode of element.childNodes) {
              processNode(childNode);
            }
          }
        };

        processNode(sectionDoc.body);

        totalChars += chapterTextLength;
        const html = sectionDoc.documentElement.outerHTML;
        const fontSize = calculatePrimaryFontSize(html, css);
        chapters.push({
          base,
          href: entry.href,
          html: html,
          text: sectionDoc.documentElement.textContent || "",
          hash: await calculateHashFromString(
            sectionDoc.documentElement.textContent || "",
          ),
          fontSize: fontSize,
        });
      }
    }

    if (book.destroyed) return empty;

    // calculate the mode of font sizes
    let fontSizes = chapters
      .filter((chapter) => chapter.html.length > 1024)
      .map((chapter) => chapter.fontSize);
    let modeFontSize = fontSizes[0] || 16;
    let maxFrequency = 1;
    const fontSizeFrequency: Record<number, number> = {};

    fontSizes.forEach((size) => {
      const roundedSize = Math.round(size * 10) / 10; // Round to nearest 0.1 for frequency counting
      fontSizeFrequency[roundedSize] =
        (fontSizeFrequency[roundedSize] || 0) + 1;
    });

    for (const [size, frequency] of Object.entries(fontSizeFrequency)) {
      const numericSize = parseFloat(size);
      if (frequency > maxFrequency) {
        maxFrequency = frequency;
        modeFontSize = numericSize;
      }
    }

    let updatedMeta = meta;
    let shouldUpdateMeta = false;

    if (!meta.chapterHashes || meta.chapterHashes.length !== chapters.length) {
      const chapterHashes = chapters.map((chapter) => chapter.hash);
      updatedMeta = { ...updatedMeta, chapterHashes };
      shouldUpdateMeta = true;
    }

    if (!hasCurrentBookComparisonFields(meta, chapters.length)) {
      const comparisonFields = await buildBookComparisonFieldsFromSections(
        chapters,
      );
      if (book.destroyed) return empty;
      updatedMeta = { ...updatedMeta, ...comparisonFields };
      shouldUpdateMeta = true;
      console.debug("getChapters: computed comparison metadata", {
        bookId: meta.id,
        title: meta.title,
        comparison: summarizeBookComparisonForDebug(updatedMeta),
      });
    }

    if (modeFontSize !== meta.primaryFontSize) {
      console.log(
        "detected primary font size",
        modeFontSize,
        "for ",
        meta.title,
        "id",
        meta.id,
      );
      updatedMeta = { ...updatedMeta, primaryFontSize: modeFontSize };
      shouldUpdateMeta = true;
    }

    if (meta.hasPublisherFontFamily !== hasPublisherFontFamily) {
      console.debug("[publisher-font] detected book font metadata", {
        bookId: meta.id,
        title: meta.title,
        hasPublisherFontFamily,
      });
      updatedMeta = { ...updatedMeta, hasPublisherFontFamily };
      shouldUpdateMeta = true;
    }

    if (shouldUpdateMeta) {
      await updateBook(updatedMeta, true);
    }

    // Save chapters and anchor mappings to IndexedDB
    if (book.destroyed) return empty;
    await chaptersStore.setItem(chaptersCacheKey, chapters);
    await anchorsStore.setItem(anchorsCacheKey, anchors);
    await chapterLocsStore.setItem(chapterLocsCacheKey, chapterLocations);
    console.debug("[reader-indexing] chapter scan finished", {
      bookId: meta.id,
      sectionCount: chapters.length,
      anchorCount: Object.keys(anchors).length,
      elapsedMs: Math.round(performance.now() - startedAt),
    });
  } catch (error) {
    if (book.destroyed) {
      console.debug("[reader-indexing] chapter scan cancelled for closed book", {
        bookId: meta.id,
        sectionsCompleted: chapters.length,
        elapsedMs: Math.round(performance.now() - startedAt),
      });
      return empty;
    }
    console.error("Error loading chapters:", error);
  }

  return { sections: chapters, locations: chapterLocations, anchors: anchors };
};

export const pullBookRevs = async () => {
  try {
    const response = await fetch("/api/pull_revs", {
      credentials: "include",
      method: "GET",
    });

    if (response.ok) {
      const revs = await response.json();
      const books = getBooks();
      Object.keys(revs).forEach((id) => {
        if (books[id]) {
          books[id]._rev = revs[id];
        } else {
          console.warn(`Book with id ${id} not found in local storage`);
        }
      });
      updateBooksCache(books);
    } else {
      console.error("Failed to update book revs", response.status);
    }
  } catch (error) {
    console.error("Error updating book revs", error);
  }
};

export const scanBookForContext = async (
  book: AnyBook,
  meta: BookMeta,
  searchTerm: string,
  progress: number,
  before: number = 250,
  after: number = 250,
  maxResults: number = 15,
): Promise<string[]> => {
  const results: string[] = [];
  try {
    await book.ready;
    const { sections } = await getChapters(meta, book);
    const wholeBook = sections.map((section) => section.text).join("\n\n");
    const bookToDate = wholeBook.slice(
      0,
      Math.ceil(progress * wholeBook.length),
    );
    const regex = new RegExp(`.{0,${before}}${searchTerm}.{0,${after}}`, "g");

    let match;
    while ((match = regex.exec(bookToDate)) !== null) {
      results.push(match[0]);
    }

    if (results.length > maxResults) {
      const first = results[0];
      const last = results[results.length - 1];
      const middleResults = results.slice(1, -1);

      const selectedMiddleResults = middleResults
        .sort(() => 0.5 - Math.random())
        .slice(0, maxResults - 2);

      return [first, ...selectedMiddleResults, last];
    }

    return results;
  } catch (error) {
    console.error("Error searching in book:", error);
    return [];
  }
};

export const uploadLog = async (
  logMessages: string[],
  clearLog: () => void,
) => {
  if (logMessages.length === 0) {
    console.log("nothing in log to upload");
    return;
  }
  console.log("uploading log...");
  try {
    const response = await fetch("/api/upload_log", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ logs: logMessages }),
    });

    if (response.ok) {
      console.log("Log successfully sent to the server, clearing");
      clearLog();
      return;
    } else {
      console.error("Failed to send logs", response.status);
    }
  } catch (error) {
    console.error("Error sending logs to server", error);
  }
};

// Add this new function to books.ts

/**
 * Handles the back navigation functionality for the e-book reader.
 *
 * @param updates - An array of UpdateRecord objects containing timestamp and progress information.
 * @param currentUpdate - The current UpdateRecord to start the back navigation from.
 * @param setSnackbarMessage - A function to set a message in the snackbar UI component.
 * @param goToHistoryUpdate - A function to navigate to a specific history update.
 * @param setUsedBack - A function to set whether updates are allowed.
 * @param historyRef - A mutable ref object to store the navigation history timestamp.
 * @returns The new timestamp after navigating back.
 */
export const handleBackNavigation = (
  updates: UpdateRecord[],
  currentUpdate: UpdateRecord,
  setSnackbarMessage: (message: SnackbarMessage) => void,
  goToHistoryUpdate: (update: UpdateRecord) => void,
  setUsedBack: (usedBack: boolean) => void,
  historyRef: React.MutableRefObject<UpdateRecord | undefined>,
): number | null => {
  const target = resolveHistoryBackTarget(updates, currentUpdate);
  if (!target) {
    console.debug("no earlier history target available", {
      currentUpdate,
      updateCount: updates.length,
    });
    return null;
  }
  const [timestamp, progress] = target;
  const formattedDate = dayjs.utc(timestamp).local().format("YYYY-MM-DD HH:mm");
  setSnackbarMessage({
    text: `Skipped back to ${formattedDate} at ${(100 * progress).toFixed(1)}%`,
    duration: 3000,
  });
  console.debug("back to", timestamp, progress);
  setUsedBack(true);
  historyRef.current = target;
  goToHistoryUpdate(target);
  return timestamp;
};

/**
 * Retrieves the most recent update record that occurred before a specified time threshold.
 *
 * @param updates - An array of UpdateRecord objects, each containing a timestamp and a value.
 * @param lastTimestamp - The reference timestamp to compare against (default: current timestamp).
 * @param minSeconds - The minimum number of seconds to look back from the lastTimestamp (default: 60 seconds).
 * @returns The most recent UpdateRecord that occurred before the specified time threshold, or [0,0] if no updates exist.
 */
export const getBack = (
  updates: UpdateRecord[],
  lastUpdate: UpdateRecord = [Date.now(), -1],
  minSeconds: number = 60,
  minProgress: number = 0.001,
): UpdateRecord => {
  if (updates.length === 0) {
    return [0, 0];
  }
  const [lastTimestamp, lastProgress] = lastUpdate;
  const index = updates.findLastIndex(
    (update) =>
      update[0] < lastTimestamp - minSeconds * 1000 &&
      Math.abs(update[1] - lastProgress) > minProgress,
  );
  if (index === -1) {
    return updates[updates.length - 1];
  }
  return updates[index];
};

/**
 * Retrieves the next update record after a given history point, moving forward in time.
 * Counterpart to getBack.
 */
export const getForward = (
  updates: UpdateRecord[],
  lastUpdate: UpdateRecord = [0, -1],
  minSeconds: number = 60,
  minProgress: number = 0.001,
): UpdateRecord | null => {
  if (updates.length === 0) {
    return null;
  }
  const [lastTimestamp, lastProgress] = lastUpdate;
  const index = updates.findIndex(
    (update) =>
      update[0] > lastTimestamp + minSeconds * 1000 &&
      Math.abs(update[1] - lastProgress) > minProgress,
  );
  if (index === -1) {
    return null;
  }
  return updates[index];
};

/**
 * Navigates forward in reading history from the current history point.
 * Counterpart to handleBackNavigation.
 */
export const handleForwardNavigation = (
  updates: UpdateRecord[],
  currentUpdate: UpdateRecord,
  setSnackbarMessage: (message: SnackbarMessage) => void,
  goToHistoryUpdate: (update: UpdateRecord) => void,
  setUsedBack: (usedBack: boolean) => void,
  historyRef: React.MutableRefObject<UpdateRecord | undefined>,
): number | null => {
  const forward = resolveHistoryForwardTarget(updates, currentUpdate);
  if (!forward) {
    console.debug("no forward history target available", {
      currentUpdate,
      updateCount: updates.length,
    });
    return null;
  }
  const [timestamp, progress] = forward;
  const formattedDate = dayjs
    .utc(timestamp)
    .local()
    .format("YYYY-MM-DD HH:mm");
  setSnackbarMessage({
    text: `Skipped forward to ${formattedDate} at ${(100 * progress).toFixed(1)}%`,
    duration: 3000,
  });
  setUsedBack(true);
  historyRef.current = forward;
  goToHistoryUpdate(forward);
  return timestamp;
};

export const guessCurrent = (
  updates?: UpdateRecord[],
  progress?: number,
): number => {
  console.log(updates);
  if (!updates || updates.length === 0 || !progress) {
    return 0;
  }

  updates.sort((a, b) => a[0] - b[0]);
  return (
    updates.findLast(
      (update, i) =>
        update[1] !== progress &&
        i > 1 &&
        updates[i][0] - updates[i - 1][0] > 10000 &&
        updates[i - 1][0] - updates[i - 2][0] > 10000 &&
        updates[i][1] > updates[i - 1][1] &&
        updates[i - 1][1] > updates[i - 2][1],
    )?.[1] ||
    updates.findLast((update, _) => update[1] !== progress)?.[1] ||
    progress
  );
};

/**
 * Adjusts the fullscreen class on the document body and the .epub-container element
 * based on the current fullscreen state.
 */
export const adjustFullscreenClass = () => {
  const doc = document.querySelector(".epub-container");
  const isFullscreen = checkIfFullscreen();
  if (doc) {
    console.log("setting fullscreen to ", isFullscreen);
    if (isFullscreen) {
      document.body.classList.add("fullscreen");
      doc.classList.add("fullscreen");
    } else {
      document.body.classList.remove("fullscreen");
      doc.classList.remove("fullscreen");
    }
  }
};

/**
 * Checks if the document is currently in fullscreen mode.
 *
 * @returns {boolean} - Returns true if the document is in fullscreen mode, otherwise false.
 */
export const checkIfFullscreen = (): boolean => {
  return document.fullscreenElement !== null;
};

/**
 * Toggles the fullscreen mode based on the desired state.
 *
 * @param {boolean | undefined} wantFullscreen - The desired fullscreen state. If undefined, the function will return without making any changes.
 */
export const changeFullscreen = (wantFullscreen: boolean | undefined) => {
  if (isNativeApp() || isIOS || isDesktop) {
    console.info("native app, iOS browser, or desktop detected; not changing DOM fullscreen");
    return;
  }
  if (wantFullscreen === undefined) {
    return;
  }

  const isFullscreen = checkIfFullscreen();
  console.debug("[fullscreen] change request", {
    wantFullscreen,
    isFullscreen,
    fullscreenElementTag: document.fullscreenElement?.tagName || null,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
    visualViewport: window.visualViewport
      ? {
          width: window.visualViewport.width,
          height: window.visualViewport.height,
          offsetTop: window.visualViewport.offsetTop,
          offsetLeft: window.visualViewport.offsetLeft,
          scale: window.visualViewport.scale,
        }
      : null,
  });

  try {
    if (wantFullscreen && !isFullscreen) {
      const requestFullscreen = document.documentElement.requestFullscreen;
      if (typeof requestFullscreen !== "function") {
        console.info("[fullscreen] requestFullscreen is unavailable");
        return false;
      }
      requestFullscreen.call(document.documentElement, { navigationUI: "hide" });
      setViewport();
      return true;
    } else if (!wantFullscreen && isFullscreen) {
      if (typeof document.exitFullscreen !== "function") {
        console.info("[fullscreen] exitFullscreen is unavailable");
        return false;
      }
      document.exitFullscreen();
      setViewport();
      return true;
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "NotAllowedError") {
      console.info(
        "Fullscreen request was denied. This may be due to lack of user interaction.",
      );
      return;
    } else {
      console.error("Fullscreen error:", error);
      return;
    }
  }
  return false;
};

export const durationFormat = (duration: number): string => {
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (hours > 100) {
    return `${hours.toFixed(0)}h`;
  } else if (hours > 10) {
    return `${hours.toFixed(1)}h`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

const updateMinutesReadInBook = async (oldBook: BookMeta) => {
  const updates = oldBook.settings.updates || [];
  const times = await getIntervalCounts(updates);
  const book = getBooks()[oldBook.id];
  book.minutesRead = times;
  book.lastUpdated = Date.now();
  console.debug("minutesPerDay: ", times, "for ", book.title);
  updateBook(book);
  return book;
};

/**
 * Retrieves the total number of minutes read for a specific book.
 *
 * @param {string} id - The unique identifier of the book.
 * @returns {Promise<number>} A promise that resolves to the total number of minutes read.
 */
export const getMinutesReadInBook = async (id: string): Promise<number> => {
  // Retrieve the book from the local storage
  let book = getBooks()[id];

  // If the book doesn't exist, return 0 minutes
  if (!book) {
    return 0;
  }

  // Check if the book's reading time needs to be updated
  // This happens if the book has never been updated or if it has been more than a minute since the last update
  if (
    !book.lastUpdated ||
    (book.timestamp && book.timestamp > book.lastUpdated + 60 * 1000)
  ) {
    // Update the book's reading time
    console.debug(
      "updating reading minutes for ",
      book.title,
      "id = ",
      book.id,
    );
    book = await updateMinutesReadInBook(book);
  }

  // Calculate the total minutes read
  // If minutesRead is undefined, use an empty object as fallback
  const total = Object.values(book.minutesRead || {}).reduce(
    (acc, minutes) => acc + minutes,
    0,
  );

  // Return the total minutes read
  return total;
};

// const getLongestChapterHtml = async (meta: BookMeta, book?: Book) => {
//   const { sections } = (await getChapters(meta, book)) || {};
//   if (!sections) {
//     return '';
//   }

//   const longestChapter = sections.reduce(
//     (max, section) => (section.html.length > max.html.length ? section : max),
//     sections[0]
//   );
//   return longestChapter.html;
// };

export const findLongestChapterHref = async (
  book: AnyBook,
): Promise<string> => {
  try {
    await book.ready;
    const chapters = book.packaging.spine as any[];
    let longestChapter = { href: "", length: 0 };

    for (const entry of chapters) {
      const section: any = await book.load(entry.href);
      if (section) {
        const chapterText = section.body.textContent || "";
        if (chapterText.length > longestChapter.length) {
          longestChapter = { href: entry.href, length: chapterText.length };
        }
      }
    }

    return longestChapter.href || (chapters[0] && chapters[0].href) || "";
  } catch (error) {
    console.error("Error finding first significant chapter:", error);
    return "";
  }
};

/**
 * Extracts all CSS content from the ePub book.
 *
 * @param book - The parsed book instance.
 * @returns An array of CSS strings.
 */
const getAllCss = async (book: AnyBook): Promise<string[]> => {
  await book.ready;
  if (book.destroyed) return [];
  await book.loaded.resources;
  if (book.destroyed) return [];
  const replacements = await book.resources.replacements();
  if (book.destroyed) return [];

  const resources = book.resources;
  const cssIndices = resources.cssUrls.map((a: string) =>
    resources.urls.findIndex((b: string) => a === b),
  );
  const cssUrls = cssIndices.map((i: number) => replacements[i]);
  const cssPromises = cssUrls.map(async (href: string) => {
    try {
      const response = await fetch(href);
      if (response.ok) {
        return await response.text();
      } else {
        console.error(`Failed to fetch CSS at ${href}: ${response.status}`);
        return "";
      }
    } catch (error) {
      console.error(`Error fetching CSS at ${href}:`, error);
      return "";
    }
  });

  const cssContents = await Promise.all(cssPromises);
  return cssContents.filter((css: string) => css.trim() !== "");
};

/**
 * Retrieves the cached CSS for a given BookMeta.
 * If not available in cache, extracts CSS using getAllCss and caches the result.
 *
 * @param {string} id - The id of the book.
 * @param {Book} withBook - An optional Book instance to use for extracting CSS.
 * @returns {Promise<string[]>} - An array of CSS strings.
 */
export const getCachedCss = async (
  id: string,
  withBook?: AnyBook,
): Promise<string[]> => {
  const cacheKey = `css-${id}`;

  // Attempt to retrieve CSS from cache
  let cachedCss = await cssStore.getItem<string[]>(cacheKey);
  if (withBook?.destroyed) return [];
  if (cachedCss) {
    console.debug(`CSS loaded from cache for book ID: ${id}`);
    return cachedCss;
  }

  // If not cached, extract CSS from the book
  const book = withBook || (await loadBook(id));
  if (!book) {
    console.error(`Book not found for ID: ${id}`);
    return [];
  }

  const cssContents = await getAllCss(book);
  if (book.destroyed) return [];

  // Cache the extracted CSS
  await cssStore.setItem<string[]>(cacheKey, cssContents);
  console.info(`CSS extracted and cached for book ID: ${id}`);

  return cssContents;
};

const clearDerivedBookCaches = async (id: string) => {
  await Promise.all([
    chaptersStore.removeItem(`chapters-${id}`),
    anchorsStore.removeItem(`anchors-${id}`),
    chapterLocsStore.removeItem(`chapterLocs-${id}`),
    cssStore.removeItem(`css-${id}`),
  ]);
  console.debug("cleared derived book caches", { id });
};

/**
 * Calculates the mode font size of the most common text selector in a book section.
 *
 * @param {string} html - The HTML content of the section to calculate the scale factor for.
 * @param {string} css - Optional CSS to apply to the container.
 * @returns {number} - The mode font size in pixels.
 */
export const calculatePrimaryFontSize = (html: string, css = ""): number => {
  try {
    // Create a detached DOM container
    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.visibility = "hidden";
    container.style.display = "block";
    container.innerHTML = html.replace(
      "</head>",
      `<style>${css}</style></head>`,
    );

    // Append to the document to ensure styles are applied
    document.body.appendChild(container);

    // Retrieve all text selectors
    const textSelectors = getAllTextSelectors(container);
    const mostCommonSelector = findMostCommonTextSelector(
      container,
      textSelectors,
    );

    // Select all elements matching the most common selector
    const elements = container.querySelectorAll(mostCommonSelector);
    const fontSizes: number[] = [];

    elements.forEach((el: Element) => {
      const fontSize = parseFloat(window.getComputedStyle(el).fontSize);
      if (!isNaN(fontSize)) {
        fontSizes.push(fontSize);
      }
    });

    // Clean up the container
    document.body.removeChild(container);

    if (fontSizes.length === 0) {
      console.warn("No valid font sizes found. Defaulting to default font size.", {
        selector: mostCommonSelector,
        defaultFontSize,
      });
      return defaultFontSize;
    }

    // Calculate the mode of the font sizes
    const fontSizeFrequency: Record<number, number> = {};
    fontSizes.forEach((size) => {
      const roundedSize = Math.round(size * 100) / 100; // Round to nearest 0.01 for frequency counting
      fontSizeFrequency[roundedSize] =
        (fontSizeFrequency[roundedSize] || 0) + 1;
    });

    let modeFontSize = fontSizes[0];
    let maxFrequency = 1;

    for (const [size, frequency] of Object.entries(fontSizeFrequency)) {
      const numericSize = parseFloat(size);
      if (frequency > maxFrequency) {
        maxFrequency = frequency;
        modeFontSize = numericSize;
      }
    }

    return modeFontSize;
  } catch (error) {
    console.error("Error calculating section scale factor:", error);
    return defaultFontSize;
  }
};

export const getAllTextSelectors = (
  doc: Document | DocumentFragment | HTMLElement,
  ignoreClass = "epubjs-ignore",
): string[] => {
  const elements = doc.querySelectorAll(
    "p, h1, h2, h3, h4, h5, h6, span, div, a, ol, ul, li",
  );
  const selectorSet = new Set<string>();

  Array.from(elements).forEach((el) => {
    const tagName = el.tagName.toLowerCase();
    if (el.className && (!ignoreClass || !el.classList.contains(ignoreClass))) {
      selectorSet.add(
        `${tagName}${el.className
          .split(" ")
          .filter(Boolean)
          .sort()
          .map((c) => `.${c}`)
          .join("")}`,
      );
    } else {
      selectorSet.add(tagName);
    }
  });
  return Array.from(selectorSet);
};

export const findMostCommonTextSelector = (
  doc: Document | DocumentFragment | HTMLElement,
  selectorList?: string[],
  ignoreClass?: string,
): string => {
  let selectors = selectorList || getAllTextSelectors(doc, ignoreClass);
  let maxCount = 0;
  let mostCommonSelector = "p";

  selectors.forEach((selector) => {
    if (selector.startsWith("p") || selector.startsWith("div")) {
      const count = doc.querySelectorAll(selector).length;
      if (count > maxCount) {
        maxCount = count;
        mostCommonSelector = selector;
      } else if (count === maxCount) {
        if (!mostCommonSelector.startsWith("p")) {
          mostCommonSelector = selector;
        } else if (selector.length > mostCommonSelector.length) {
          mostCommonSelector = selector;
        }
      }
    }
  });

  const docAsElement = doc as Element;
  const rootIsReaderWrapper =
    typeof docAsElement.matches === "function" &&
    docAsElement.matches("div.epubjs-ignore");
  const hasReaderWrapper =
    rootIsReaderWrapper || !!doc.querySelector("div.epubjs-ignore");

  return hasReaderWrapper
    ? `div.epubjs-ignore ${mostCommonSelector}`
    : mostCommonSelector;
};

export const getScaleFactor = (
  element: Element,
  desiredSize: number,
): number => {
  const elementComputedStyle = window.getComputedStyle(element);
  const elementComputedSize = parseFloat(elementComputedStyle.fontSize);
  const scaleFactor = desiredSize / elementComputedSize;

  return scaleFactor;
};

/**
 * Finds the nth-highest non-unique percentage value from a list of update records.
 *
 * @param updates - An array of UpdateRecord objects containing timestamp and percentage information.
 * @returns The nth-highest non-unique percentage value, or undefined if there are fewer than n unique values.
 */
export const getNthHighestPercentage = (
  updates: UpdateRecord[],
  n: number = 3,
): number | undefined => {
  if (updates.length < n) {
    return undefined;
  }

  // Sort the updates by percentage in descending order
  const sortedPercentages = updates
    .map(([_, percentage]) => percentage)
    .sort((a, b) => b - a);

  // Find the nth-highest non-unique value
  let count = 0;
  let lastValue = Infinity;

  for (const percentage of sortedPercentages) {
    if (percentage !== lastValue) {
      count++;
      if (count === n) {
        return percentage;
      }
      lastValue = percentage;
    }
  }

  // If there are fewer than n unique values, return undefined
  return undefined;
};

export const calculateEngagement = async (book: BookMeta): Promise<number> => {
  const focusMetrics = getBookFocusMetrics(book);
  if (!focusMetrics) {
    return 0;
  }

  return focusMetrics.focus;
};

const getIntervalCounts = async (
  updates: UpdateRecord[] | undefined,
): Promise<Record<number, number>> => {
  if (!updates) {
    return {};
  }

  const minutesPerDay: Record<number, number> = {};

  updates
    .sort((a, b) => a[0] - b[0])
    .forEach(([timestamp, _], i, arr) => {
      if (i === 0) return; // Skip the first element

      // TODO: proper local time
      const offset = 1000 * 60 * 60 * 4;
      const prevTimestamp = arr[i - 1][0];
      const day = Math.floor((timestamp - offset) / (24 * 60 * 60 * 1000));
      const prevDay = Math.floor(
        (prevTimestamp - offset) / (24 * 60 * 60 * 1000),
      );

      if (day !== prevDay) {
        // If the day has changed, ensure the previous day is counted
        minutesPerDay[prevDay] =
          (minutesPerDay[prevDay] || 0) +
          countMinutesInInterval(
            arr,
            prevDay * 24 * 60 * 60 * 1000 + offset,
            day * 24 * 60 * 60 * 1000 + offset,
          );
      }

      // Count the current day

      const lastMidnight =
        Math.floor((Date.now() - offset) / (24 * 60 * 60 * 1000)) *
          1000 *
          60 *
          60 *
          24 +
        offset;
      minutesPerDay[Math.floor((Date.now() - offset) / (24 * 60 * 60 * 1000))] =
        countMinutesInInterval(
          arr,
          lastMidnight,
          lastMidnight + 24 * 60 * 60 * 1000,
        );
    });

  return minutesPerDay;
};

const countMinutesInInterval = (
  updates: UpdateRecord[] | undefined,
  start: number = 0,
  end: number = Infinity,
) => {
  if (!updates) {
    return 0;
  }
  const updatesInInterval = updates
    ?.filter(([timestamp, _]) => timestamp >= start && timestamp <= end)
    .sort((a, b) => a[0] - b[0]);
  // get list of differences between consecutive updates in both time and prop
  if (updatesInInterval.length < 2) {
    return 0;
  }
  let diffs = updatesInInterval.slice(1).map((current, i) => {
    const prev = updatesInInterval[i];
    return [current[0] - prev[0], current[1] - prev[1]] as UpdateRecord;
  });
  diffs = diffs.filter(
    ([timeDiff, progDiff]) =>
      timeDiff < 300000 && timeDiff > 0 && Math.abs(progDiff) < 1,
  );
  const totalTime = diffs.reduce((acc, [timeDiff, _]) => acc + timeDiff, 0);
  return Math.ceil(totalTime / (60 * 1000));
};

const getStartEndOfDay = (daysAgo: number) => {
  const startOfDay = dayjs
    .utc()
    .local()
    .subtract(daysAgo, "day")
    .startOf("day")
    .valueOf();
  const endOfDay = dayjs
    .utc()
    .local()
    .subtract(daysAgo, "day")
    .endOf("day")
    .valueOf();
  return { startOfDay, endOfDay };
};
const getDayAbbreviation = (date: dayjs.Dayjs) => {
  const day = date.format("dd"); // 'dd' returns the two-letter day abbreviation
  return day.charAt(0).toUpperCase();
};

export const aggregateWeeklyTime = async (
  days: number = 7,
  label: "dayOfWeek" | "dayOfMonth" = "dayOfWeek",
) => {
  //   const books = { ...getArchivedBooks(), ...getBooks() };
  const books = getBooks();

  const totalTimePerDay = Array.from({ length: days }, (_, i) => {
    const date = dayjs().subtract(i, "day");
    const { startOfDay, endOfDay } = getStartEndOfDay(i);
    const totalTimeForDay = Object.values(books).reduce((acc, book) => {
      return (
        acc +
        countMinutesInInterval(
          book.settings.updates || [],
          startOfDay,
          endOfDay,
        )
      );
    }, 0);
    let day = "";
    if (label === "dayOfWeek") {
      day = getDayAbbreviation(date);
    } else {
      day = date.format("D");
    }
    return { label: day, value: totalTimeForDay };
  }).reverse();
  return totalTimePerDay;
};

export const replaceBookFile = async (id: string, file: File) => {
  console.log("replacing file for book with id: ", id, file);
  return new Promise((resolve, reject) => {
    const oldBook = getBooks()[id];
    const reader = new FileReader();

    reader.onload = async (event) => {
      const arrayBuffer = event.target?.result;
      if (arrayBuffer && typeof arrayBuffer !== "string") {
        const hash = await calculateFileHashFromArrayBuffer(arrayBuffer);
        try {
          const book = createBook(arrayBuffer);
          const metadata = await book.loaded.metadata;
          const title = metadata.title;
          const author = metadata.creator;
          let cssContents: string[] = [];
          let hasPublisherFontFamily = false;
          let coverUrl = "";
          try {
            coverUrl = (await book.coverUrl()) ?? "";
          } catch (coverError) {
            console.error("Error extracting cover:", coverError);
          }
          try {
            cssContents = await getAllCss(book);
            hasPublisherFontFamily = cssContents.some(cssHasPublisherFontFamily);
            console.debug("[publisher-font] detected font metadata on replace", {
              bookId: id,
              title,
              hasPublisherFontFamily,
              cssFileCount: cssContents.length,
            });
          } catch (cssError) {
            console.warn("[publisher-font] failed to inspect EPUB CSS on replace", {
              bookId: id,
              title,
              error: cssError,
            });
          }
          const coverResponse = await fetch(coverUrl);
          const coverBlob = await coverResponse.blob();
          let thumbBlob: Blob = coverBlob;
          try {
            thumbBlob = await createThumbnail(coverBlob);
          } catch (thumbnailError) {
            console.error("replaceBookFile: failed to create replacement cover thumbnail", {
              bookId: id,
              title,
              coverType: coverBlob.type || null,
              coverSize: coverBlob.size,
              error: thumbnailError,
            });
          }
          console.debug("replaceBookFile: prepared replacement cover thumbnail", {
            bookId: id,
            title,
            sourceCoverSize: coverBlob.size,
            thumbnailSize: thumbBlob.size,
            thumbnailType: thumbBlob.type || null,
          });

          const saveFileResult = saveFile(id, file);
          const saveFileCoverResult = saveFile(`cover-${id}`, thumbBlob);
          const deleteResult = deleteFile(`locations-${id}`, "ePubObjects");
          console.debug("delete locations result: ", deleteResult);
          const [words, chapterWords] = await getTotalWordCount(book);
          console.log("total word count:", words);
          const replacementTimestamp = Date.now();
          const newBook = {
            ...oldBook,
            title: title ?? file.name.replace(/\.[^/.]+$/, ""),
            hash: hash,
            timestamp: replacementTimestamp,
            coverUpdatedAt: replacementTimestamp,
            coverImageHash: (await calculateFileHash(thumbBlob)) as string,
            wordCount: words,
            languageCode: metadata.language,
            chapterWordCount: chapterWords,
            hasPublisherFontFamily,
            author: author,
            pubDate: metadata.pubdate,
            description: metadata.description,
            cloud: oldBook?.cloud?.googleDrive
              ? {
                  ...oldBook.cloud,
                  googleDrive: {
                    ...oldBook.cloud.googleDrive,
                    localFileAvailable: true,
                    downloadedAt: replacementTimestamp,
                  },
                }
              : oldBook?.cloud,
          };
          const identifiedBook = await ensureBookIdentityFields(
            newBook,
            getCurrentUser()?.email,
            {
              preserveWorkId: true,
              preserveEditionId: false,
            },
          );
          let comparisonReadyBook = identifiedBook;
          try {
            const comparisonFields = await deriveComparisonFieldsForBook(book);
            comparisonReadyBook = {
              ...identifiedBook,
              ...comparisonFields,
            };
          } catch (comparisonError) {
            console.warn("replaceBookFile: failed to derive comparison metadata before sync", {
              bookId: id,
              title,
              error: comparisonError,
            });
          }
          const bookData = (await getBookData(comparisonReadyBook)) as BookMeta;
          console.debug(
            "addBookFromFile, bookData = ",
            bookData,
            " newBook = ",
            comparisonReadyBook,
          );

          const mergedBook = mergeBookMeta(
            mergeBookMeta(defaultBookMeta, bookData),
            comparisonReadyBook,
          );
          console.info("mergedBook = ", mergedBook);
          const updateBookResult = updateBook(mergedBook);
          await Promise.all([
            saveFileResult,
            saveFileCoverResult,
            updateBookResult,
          ]);
          await clearDerivedBookCaches(mergedBook.id);
          if (cssContents.length > 0) {
            await cssStore.setItem<string[]>(`css-${mergedBook.id}`, cssContents);
          }
          await getChapters(stripDerivedTextMetadata(mergedBook), book);
          resolve(title);
        } catch (error) {
          console.log("Error adding book:", error);
          resolve(""); // Resolve with an empty string on error to indicate failure
        }
      } else {
        resolve(""); // Resolve with an empty string if arrayBuffer is not valid
      }
    };

    reader.onerror = (error) => {
      console.log("Error reading file:", error);
      reject(error);
    };

    reader.readAsArrayBuffer(file);
  });
};

export const createThumbnail = async (file: File | Blob): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      if (ctx) {
        canvas.width = 320;
        canvas.height = 480;
        ctx.drawImage(img, 0, 0, 320, 480);
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error("Thumbnail creation failed"));
          }
        }, "image/jpeg");
      } else {
        reject(new Error("Canvas context not available"));
      }
    };

    img.onerror = (err) => {
      reject(err);
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Deletes a book and updates any related translations.
 *
 * @param id - The ID of the book to delete
 * @returns Promise resolving to the updated BookCollection
 *
 * @description
 * This function:
 * 1. Deletes the book's files (main file, cover, locations)
 * 2. Updates the book's status to 'trash'
 * 3. Removes this book from the translations list of any related translated versions
 * 4. Returns the updated book collection
 */
export const deleteBook = async (id: string): Promise<BookCollection> => {
  const books = getBooks();
  if (id && books[id]) {
    console.log("deleting book with id: ", id);
    const bookToDelete = books[id];
    await recordLocalDriveBookDeletion(bookToDelete);

    // Delete associated files
    if (bookToDelete.id) {
      deleteFile(bookToDelete.id);
      deleteFile(`cover-${bookToDelete.id}`);
      deleteFile(`locations-${bookToDelete.id}`, "ePubObjects");
      await clearDerivedBookCaches(bookToDelete.id);
    }

    // Update translations for related books
    if (bookToDelete.translations) {
      // Get all translation IDs except the one being deleted
      const translationIds = Object.values(bookToDelete.translations).filter(
        (tid) => tid !== id,
      );

      // Update each related translation
      console.log("translationIds = ", translationIds);
      console.log("books = ", books);
      for (const translationId of translationIds) {
        console.log("translationId = ", translationId);
        if (books[translationId]) {
          const translatedBook = books[translationId];
          console.log("translatedBook = ", translatedBook);
          if (translatedBook.translations) {
            console.log(
              "translatedBook.translations = ",
              translatedBook.translations,
            );
            // Create new translations object filtering out the deleted book
            const updatedTranslations = Object.fromEntries(
              Object.entries(translatedBook.translations).filter(
                ([_, bookId]) => bookId !== id,
              ),
            );
            console.log("updatedTranslations = ", updatedTranslations);
            console.log("translatedBook = ", translatedBook);

            // Update the translated book with modified translations list
            await updateBook(
              {
                ...translatedBook,
                translations: updatedTranslations,
              },
              true,
            );

            console.log(
              "updated translations for book with id:",
              translationId,
              "to:",
              updatedTranslations,
            );
          }
        }
      }
    }

    // Mark the book as deleted
    const bookDeleted = {
      ...bookToDelete,
      location: "trash",
      archived: undefined,
      translations: undefined, // Remove translations list from deleted book
    };

    await updateBook(bookDeleted, true);
    pruneTrashEntries();
    console.log("deleted book with id: ", id, " bookDeleted = ", bookDeleted);
    return getBooks();
    // console.log('books = ', books);
    // return { ...books, [id]: bookDeleted };
  }
  return books;
};

/**
 * Resets the book with the given ID by overwriting its data with default values.
 *
 * @param {string} id - The unique identifier of the book to reset.
 * @returns {Promise<BookMeta | null>} - A promise that resolves when the reset is complete.
 *
 * @description
 * This function resets the specified book's data to default values,
 * effectively removing any customizations or notes. It updates both
 * the local cache and the server to ensure consistency.
 */
export const resetBook = async (id: string): Promise<BookMeta | null> => {
  try {
    const books = getBooks();

    const existingBook = books[id];
    if (!existingBook) {
      console.warn(`resetBook: Book with id ${id} does not exist.`);
      return null;
    }

    const existingBookFile = (await loadFile(existingBook.id)) as File;
    if (!existingBookFile) {
      console.warn(`resetBook: Could not load book file with id ${id}.`);
      return null;
    }

    const oldBook = getBook(id);
    if (!oldBook) {
      console.warn(`resetBook: Failed to get book with id ${id}.`);
      return null;
    }
    logger.debug("[library-reset] reimporting in source folder", {
      bookId: id,
      parentId: oldBook.parentId || "root",
      activeGroupId: getPrefs()?.activeGroupId,
      hash: oldBook.hash,
    });
    const newId = await addBookFromFile(existingBookFile, true, "", oldBook.parentId || "root");
    if (!newId) {
      console.warn(`resetBook: Failed re-adding book file with id ${id}.`);
      return null;
    }

    const newBook = getBook(newId);
    if (!newBook) {
      console.warn(`resetBook: Failed to get book with id ${newId}.`);
      return null;
    }

    if (oldBook.id !== newBook.id) {
      await updateBook({ ...oldBook, location: "trash" });
      pruneTrashEntries();
    }

    console.log(
      `resetBook: Finished resetting local data for book with id ${id}.`,
    );

    // Push the reset data to the server
    const user = getCurrentUser();
    if (user) {
      const response = await fetch("/api/reset_book", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(oldBook),
      });

      if (response.ok) {
        console.log(
          `resetBook: Successfully reset book with id ${id} on server.`,
        );
        return newBook;
      } else {
        console.error(
          `resetBook: Failed to reset book with id ${id} on server. Status: ${response.status}`,
        );
        return null;
      }
    } else {
      console.warn("resetBook: No user found. Skipping server update.");
      return newBook;
    }
  } catch (error) {
    console.error(`resetBook: Error resetting book with id ${id}:`, error);
    return null;
  }
};

/**
 * Extracts the base CFI from a given CFI string.
 * @param cfi - The CFI string to extract the base from.
 * @returns The base CFI string, or an empty string if not found.
 */
export const extractCfiBase = (cfi?: string) => {
  // Section CFIs can include an epub.js assertion after the spine step,
  // e.g. /6/462[xhtml0229]!. Strip the assertion but keep the numeric base
  // so location CFIs and note CFIs still resolve to the same section key.
  const match = cfi?.match(
    /(?:epubcfi\()?\/(\d+\/\d+)(?:\[[^\]]+\])?(?:!|$)/,
  );
  const extracted = match ? `/${match[1]}` : "";
  if (!extracted && cfi) {
    logger.debug("extractCfiBase: failed to resolve section base", { cfi });
  }
  return extracted;
};

/**
 * Adds a book from a given file.
 * Associates the book with the current user.
 * @param {File} file - The EPUB file to add.
 * @param {boolean} isReset - Whether the book is being reset.
 * @param {string} useId - The ID to use for the book, or a random UUID if not provided.
 * @returns {Promise<string>} - The title of the added book, or an empty string if failed.
 */

export const addBookFromFile = async (
  file: File,
  isReset: boolean = false,
  useId: string = "",
  parentId?: string,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    let importStage = "waiting-for-file-read";
    const fileDetails = () => ({
      stage: importStage,
      fileName: file.name || null,
      fileSize: file.size,
      fileType: file.type || null,
      fileLastModified: file.lastModified || null,
      isReset,
      useIdPresent: Boolean(useId),
      requestedParentId: parentId || null,
    });

    reader.onload = async (event) => {
      importStage = "file-read-complete";
      const arrayBuffer = event.target?.result;
      if (arrayBuffer && typeof arrayBuffer !== "string") {
        importStage = "hash-file";
        const hash = await calculateFileHashFromArrayBuffer(arrayBuffer);
        try {
          importStage = "create-book";
          const useParentId = parentId || getPrefs()?.activeGroupId || "root";
          const book = createBook(arrayBuffer);
          importStage = "load-metadata";
          const metadata = await book.loaded.metadata;
          const title = metadata.title || file.name.replace(/\.[^/.]+$/, "");
          const author = metadata.creator || "Unknown";
          let coverUrl = "";
          try {
            importStage = "extract-cover-url";
            coverUrl = (await book.coverUrl()) ?? "";
          } catch (coverError) {
            console.error("Error extracting cover:", coverError);
          }

          // Use deterministic UUID based on current user email, and book author and title.
          const currentUser = getCurrentUser();
          const seed = `${currentUser?.email}-${author}-${title}`;
          const id =
            useId ||
            (currentUser?.email &&
              metadata.title &&
              metadata.creator &&
              createDeterministicUUIDv4(seed)) ||
            crypto.randomUUID();

          importStage = "fetch-cover";
          const coverResponse = await fetch(coverUrl);
          importStage = "read-cover-blob";
          const coverBlob = await coverResponse.blob();
          let thumbBlob: Blob | null = null;
          try {
            importStage = "create-thumbnail";
            thumbBlob = await createThumbnail(coverBlob);
          } catch (error) {
            console.error("Error creating thumbnail:", error);
            thumbBlob = coverBlob;
          }
          //   await saveFile(`thumbnail-${file.name}`, coverBlob);

          let cssContents: string[] = [];
          let hasPublisherFontFamily = false;
          try {
            importStage = "inspect-css";
            cssContents = await getAllCss(book);
            hasPublisherFontFamily = cssContents.some(cssHasPublisherFontFamily);
            console.debug("[publisher-font] detected font metadata on import", {
              bookId: id,
              title,
              hasPublisherFontFamily,
              cssFileCount: cssContents.length,
            });
          } catch (cssError) {
            console.warn("[publisher-font] failed to inspect EPUB CSS on import", {
              bookId: id,
              title,
              error: cssError,
            });
          }

          importStage = "count-words";
          const [words, chapterWords] = await getTotalWordCount(book);
          console.log("total word count:", words);
          const favorite = mergePrefs(getPrefs()).favorite || {};
          const favoriteFontFamily =
            favorite.fontFamily && favorite.fontFamily !== "default"
              ? favorite.fontFamily
              : defaultFavorite.fontFamily || "serif";
          const rawNewBook: BookMeta = {
            id,
            title,
            parentId: useParentId,
            settings: {
              ...defaultBookSettings,
              ...favorite,
              fontFamily: hasPublisherFontFamily ? "default" : favoriteFontFamily,
            },
            createdAt: Date.now(),
            notes: {},
            hash: hash,
            timestamp: Date.now(),
            wordCount: words,
            languageCode: metadata.language,
            chapterWordCount: chapterWords,
            visibleAt: {},
            hasPublisherFontFamily,
            author: author,
            publishedDate: metadata.pubdate,
            description: metadata.description,
          };
          importStage = "identity-fields";
          const newBook = await ensureBookIdentityFields(
            rawNewBook,
            currentUser?.email,
          );
          let comparisonReadyBook = newBook;
          try {
            importStage = "derive-comparison-fields";
            const comparisonFields = await deriveComparisonFieldsForBook(book);
            comparisonReadyBook = {
              ...newBook,
              ...comparisonFields,
            };
          } catch (comparisonError) {
            console.warn("addBookFromFile: failed to derive comparison metadata before sync", {
              bookId: id,
              title,
              error: comparisonError,
            });
          }
          importStage = "load-existing-book-data";
          const bookData = (await getBookData(comparisonReadyBook)) as BookMeta;
          const preparedBook = isReset
            ? {
                ...comparisonReadyBook,
                notes: bookData.notes || {},
              }
            : mergeBookMeta(comparisonReadyBook, bookData);
          if (!isReset) {
            console.info("addBookFromFile: prepared import book", {
              generatedId: id,
              resolvedId: preparedBook.id,
              reusedExistingId: preparedBook.id !== id,
              title,
              hash,
              remoteBook: summarizeBookForDebug(bookData),
              preparedBook: summarizeBookForDebug(preparedBook),
            });
          } else {
            console.log("resetBook, preparedBook = ", preparedBook);
          }
          const mergedBook = {
            ...mergeBookMeta(defaultBookMeta, preparedBook),
            location: getPrefs()?.activeGroupId || "",
            timestamp: Date.now(),
            archived: undefined,
          };
          // updateBook also merges local artwork metadata. Reconcile its bytes
          // first so importing the EPUB cannot silently replace a pending edit.
          importStage = "reconcile-cover";
          const existingCover = mergeBookMeta(getBook(mergedBook.id), mergedBook);
          let preserveExistingCover = false;
          if (existingCover.coverImageHash || existingCover.coverUpdatedAt) {
            if (!isReset && existingCover.coverImageHash) {
              const existingThumbnail = await loadFile(`cover-${mergedBook.id}`);
              const existingThumbnailHash = existingThumbnail
                ? (await calculateFileHash(existingThumbnail)) as string
                : undefined;
              preserveExistingCover = existingThumbnailHash === existingCover.coverImageHash;
              if (!preserveExistingCover) {
                logger.warn("[library-cover] import could not preserve edited artwork", {
                  bookId: mergedBook.id,
                  expectedHash: existingCover.coverImageHash,
                  actualHash: existingThumbnailHash || null,
                  thumbnailBytes: existingThumbnail?.size || 0,
                });
              }
            }
            mergedBook.coverImageHash = preserveExistingCover
              ? existingCover.coverImageHash
              : (await calculateFileHash(thumbBlob)) as string;
            mergedBook.coverUpdatedAt = preserveExistingCover
              ? existingCover.coverUpdatedAt
              : Math.max(Date.now(), (existingCover.coverUpdatedAt || 0) + 1);
            logger.info("[library-cover] reconciled artwork on import", {
              bookId: mergedBook.id,
              isReset,
              preservedExistingCover: preserveExistingCover,
              coverImageHash: mergedBook.coverImageHash,
              coverUpdatedAt: mergedBook.coverUpdatedAt,
            });
          }
          importStage = "persist-book";
          const updateBookResult = updateBook(mergedBook, true);
          const saveFileResult = saveFile(mergedBook.id, file);
          const saveFileCoverResult = preserveExistingCover
            ? Promise.resolve()
            : saveFile(`cover-${mergedBook.id}`, thumbBlob);
          await Promise.all([
            saveFileResult,
            saveFileCoverResult,
            updateBookResult,
          ]);
          importStage = "clear-derived-caches";
          await clearDerivedBookCaches(mergedBook.id);
          if (cssContents.length > 0) {
            importStage = "persist-css";
            await cssStore.setItem<string[]>(`css-${mergedBook.id}`, cssContents);
          }
          importStage = "build-chapters";
          await getChapters(stripDerivedTextMetadata(mergedBook), book);

          importStage = "complete";
          resolve(mergedBook.id);
        } catch (error) {
          logger.error("EPUB import failed", {
            ...fileDetails(),
            error: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : undefined,
          });
          resolve(""); // Resolve with an empty string on error to indicate failure
        }
      } else {
        logger.error("EPUB import failed: FileReader returned an invalid result", fileDetails());
        resolve(""); // Resolve with an empty string if arrayBuffer is not valid
      }
    };

    reader.onerror = (error) => {
      importStage = "file-read-error";
      logger.error("EPUB file read failed", {
        ...fileDetails(),
        error: reader.error?.message || String(error),
        errorName: reader.error?.name || null,
      });
      reject(reader.error || error);
    };

    reader.readAsArrayBuffer(file);
  });
};

/**
 * Adds a translated version of the book with the given ID.
 * @param id - The ID of the book to add the version to.
 * @param file - The file containing the version.
 * @returns The ID of the new book, or null if failed.
 */
export const addTranslatedVersion = async (
  id: string,
  file: File,
): Promise<string | null> => {
  console.debug(
    "addTranslatedVersion: adding translated version with id: ",
    id,
  );
  const newId = await addBookFromFile(file);
  if (newId) {
    const oldBook = getBook(id);
    const newBook = getBook(newId);
    console.debug(
      "addTranslatedVersion: oldBook = ",
      oldBook,
      " newBook = ",
      newBook,
    );
    if (oldBook && newBook) {
      if (
        oldBook.languageCode &&
        newBook.languageCode &&
        oldBook.languageCode.toLowerCase() !==
          newBook.languageCode.toLowerCase()
      ) {
        const translations = {
          ...(oldBook.translations || {}),
          [oldBook.languageCode.toLowerCase().split("-")[0]]: id,
          [newBook.languageCode.toLowerCase().split("-")[0]]: newId,
        };
        await updateBook({ ...oldBook, translations: translations });
        await updateBook({ ...newBook, translations: translations });
        console.log("added translated version with id: ", newId);
        return newId;
      } else {
        console.warn(
          "addTranslatedVersion: No language code for old or new book",
          id,
          newId,
        );
        return null;
      }
    }
  }
  return null;
};

/**
 * Refreshes the annotations by updating them with the provided notes.
 *
 * @param {Annotations} annotations - The annotations object to update.
 * @param {NotesGroup} notes - The group of notes containing the updated annotation details.
 */
export const refreshAnnotations = async (
  annotations: Annotations,
  notes: NotesGroup,
  bookMeta: BookMeta,
  sectionStep: string | null,
  iframeDoc?: Document,
  onlyReveal?: boolean,
) => {
  if (iframeDoc) {
    iframeDoc.normalize();
  }
  const matchingNotes = Object.values(mergeNotes({}, notes)).filter(
    (note) =>
      !note.deleted &&
      (!sectionStep || extractCfiBase(note.cfiRange) === sectionStep),
  );
  if (sectionStep && typeof annotations.removeWhere === "function") {
    const activeNoteIds = new Set(matchingNotes.map((note) => note.id));
    const removedStaleCount = annotations.removeWhere((annotation) => {
      if (annotation.type !== "highlight") {
        return false;
      }
      if (extractCfiBase(annotation.cfiRange) !== sectionStep) {
        return false;
      }
      const annotationId = annotation.data?.id;
      if (typeof annotationId !== "string") {
        return false;
      }
      if (annotationId.startsWith("__custom_selection_preview")) {
        return false;
      }
      return !activeNoteIds.has(annotationId);
    });
    if (removedStaleCount > 0) {
      logger.debug("refreshAnnotations: pruned stale note annotations", {
        sectionStep,
        removedStaleCount,
        matchingNoteCount: matchingNotes.length,
      });
    }
  }
  matchingNotes.sort((a, b) => {
    try {
      return EpubCFI.prototype.compare(b.cfiRange, a.cfiRange);
    } catch (error) {
      logger.warn("refreshAnnotations: failed to sort notes by CFI", {
        sectionStep,
        noteA: { id: a.id, cfiRange: a.cfiRange },
        noteB: { id: b.id, cfiRange: b.cfiRange },
        error,
      });
      return 0;
    }
  });
  matchingNotes.forEach((note) => {
    updateAnnotation(annotations, note, bookMeta, iframeDoc, onlyReveal);
  });
  return matchingNotes.length;
};

export const REACTION_BADGE_DISMISSED_EVENT =
  "wepub:reaction-badge-dismissed";

const INLINE_NOTE_MARKER_ATTRIBUTE = "data-inline-note-marker";
const INLINE_NOTE_MARKER_SELECTOR = `[${INLINE_NOTE_MARKER_ATTRIBUTE}="true"]`;
const REACTION_SPACER_ATTRIBUTE = "data-reaction-spacer";
const REACTION_SPACER_SELECTOR = `[${REACTION_SPACER_ATTRIBUTE}="true"]`;
const isElementNode = (value: unknown): value is Element =>
  !!value &&
  typeof (value as Node).nodeType === "number" &&
  (value as Node).nodeType === Node.ELEMENT_NODE;

const getReactionContainerSelector = (noteId: string) =>
  `g[data-reaction-note-id="${escapeSelectorAttrValue(noteId)}"]`;

const getReactionSpacerSelector = (noteId: string) =>
  `${REACTION_SPACER_SELECTOR}[data-note-id="${escapeSelectorAttrValue(noteId)}"]`;

const clearReactionArtifacts = (
  noteId: string,
  iframeDoc?: Document,
  reason?: string,
  extra: Record<string, unknown> = {},
) => {
  const existingContainers = Array.from(
    document.querySelectorAll(getReactionContainerSelector(noteId)),
  );
  existingContainers.forEach((container) => container.remove());

  const existingSpacers = iframeDoc
    ? Array.from(iframeDoc.querySelectorAll(getReactionSpacerSelector(noteId)))
    : [];
  existingSpacers.forEach((spacer) => spacer.remove());

  if (existingContainers.length > 0 || existingSpacers.length > 0) {
    logger.debug("clearReactionArtifacts: removed reaction UI", {
      noteId,
      reason,
      removedContainerCount: existingContainers.length,
      removedSpacerCount: existingSpacers.length,
      ...extra,
    });
  }
};

const syncReactionSpacer = (
  note: Pick<Note, "id">,
  iframeDoc: Document | undefined,
  cfiRange: string,
  spacerWidth: number,
): HTMLSpanElement | null => {
  if (!iframeDoc) {
    return null;
  }

  const existingSpacers = Array.from(
    iframeDoc.querySelectorAll(getReactionSpacerSelector(note.id)),
  );
  existingSpacers.forEach((spacer) => spacer.remove());

  if (!(spacerWidth > 0)) {
    logger.debug("syncReactionSpacer: spacer width was not positive", {
      noteId: note.id,
      cfiRange,
      spacerWidth,
    });
    return null;
  }

  let range: Range | null = null;
  let resolvedWith: "epubjs-ignore" | "none" | "unresolved" = "unresolved";
  try {
    range = new EpubCFI(cfiRange).toRange(iframeDoc, "epubjs-ignore");
    if (range) {
      resolvedWith = "epubjs-ignore";
    }
  } catch (_error) {}

  if (!range) {
    try {
      range = new EpubCFI(cfiRange).toRange(iframeDoc, "");
      if (range) {
        resolvedWith = "none";
      }
    } catch (_error) {}
  }

  if (!range) {
    logger.warn("syncReactionSpacer: unable to resolve range for spacer", {
      noteId: note.id,
      cfiRange,
      spacerWidth,
      existingSpacerCount: existingSpacers.length,
    });
    return null;
  }

  const spacer = iframeDoc.createElement("span");
  spacer.classList.add("epubjs-ignore");
  spacer.setAttribute(REACTION_SPACER_ATTRIBUTE, "true");
  spacer.dataset.noteId = note.id;
  spacer.id = `reaction-spacer-${note.id}`;
  spacer.setAttribute("aria-hidden", "true");
  spacer.textContent = "";
  spacer.setAttribute(
    "style",
    [
      "display:inline-block",
      `width:${Math.ceil(spacerWidth)}px`,
      `min-width:${Math.ceil(spacerWidth)}px`,
      "height:1px",
      "line-height:1px",
      "font-size:1px",
      "overflow:hidden",
      "opacity:0",
      "pointer-events:none",
      "user-select:none",
      "vertical-align:baseline",
    ].join(";"),
  );

  range.collapse(false);
  range.insertNode(spacer);

  logger.debug("syncReactionSpacer: inserted empty spacer after reaction anchor", {
    noteId: note.id,
    cfiRange,
    spacerWidth: Math.ceil(spacerWidth),
    resolvedWith,
    existingSpacerCount: existingSpacers.length,
    spacerTextLength: spacer.textContent?.length || 0,
  });

  return spacer;
};

const getFirstRenderableRectForNode = (node: Node | null): DOMRect | null => {
  let current: Node | null = node;

  while (current) {
    if (current.nodeType === Node.TEXT_NODE) {
      const text = current.textContent || "";
      for (let index = 0; index < text.length; index += 1) {
        if (!text[index] || /\s/.test(text[index])) {
          continue;
        }
        const range = current.ownerDocument?.createRange();
        if (!range) {
          break;
        }
        range.setStart(current, index);
        range.setEnd(current, index + 1);
        const rect = range.getBoundingClientRect();
        if (rect.width > 0 || rect.height > 0) {
          return rect;
        }
      }
    } else if (current instanceof Element) {
      const rect = current.getBoundingClientRect();
      if (rect.width > 0 || rect.height > 0) {
        return rect;
      }
      const childRect = getFirstRenderableRectForNode(current.firstChild);
      if (childRect) {
        return childRect;
      }
    }

    current = current.nextSibling;
  }

  return null;
};

const normalizeTextForComparison = (value?: string | null): string =>
  (value || "").replace(/\s+/g, " ").trim();

const rangeRectCount = (range: Range | null): number => {
  if (!range || typeof range.getClientRects !== "function") {
    return 0;
  }
  try {
    return range.getClientRects().length || 0;
  } catch (_error) {
    return 0;
  }
};

const excerptText = (value?: string | null, maxLength = 120): string => {
  const normalized = normalizeTextForComparison(value);
  if (!normalized) {
    return "";
  }
  return normalized.length > maxLength
    ? `${normalized.slice(0, maxLength)}...`
    : normalized;
};

const excerptNodeAroundOffset = (
  node: Node | null | undefined,
  offset?: number | null,
): string => {
  const text = node?.textContent || "";
  if (!text) {
    return "";
  }
  if (offset === undefined || offset === null || Number.isNaN(offset)) {
    return excerptText(text);
  }
  const start = Math.max(0, offset - 30);
  const end = Math.min(text.length, offset + 30);
  return excerptText(text.slice(start, end));
};

type ResolvedNoteCfiCandidate = {
  cfiRange: string;
  endOffset: number | null;
  endSnippet: string;
  label: "raw" | "adjusted";
  normalizedText: string;
  rectCount: number;
  resolvedText: string;
  resolvedWith: "epubjs-ignore" | "none" | "unresolved";
  score: number;
  startOffset: number | null;
  startSnippet: string;
};

const scoreResolvedCandidate = (
  expectedText: string,
  candidateText: string,
  rectCount: number,
): number => {
  const expected = normalizeTextForComparison(expectedText);
  const candidate = normalizeTextForComparison(candidateText);

  if (!candidate) {
    return rectCount > 0 ? 1 : 0;
  }
  if (!expected) {
    return rectCount > 0 ? 5 : 1;
  }
  if (candidate === expected) {
    return 100;
  }
  if (expected.includes(candidate)) {
    return 60 + Math.min(20, candidate.length);
  }
  if (candidate.includes(expected)) {
    return 55 + Math.min(20, expected.length);
  }

  const sharedPrefixLength = (() => {
    const limit = Math.min(expected.length, candidate.length);
    let index = 0;
    while (index < limit && expected[index] === candidate[index]) {
      index += 1;
    }
    return index;
  })();

  if (sharedPrefixLength >= 4) {
    return 30 + Math.min(20, sharedPrefixLength);
  }

  return rectCount > 0 ? 10 : 2;
};

const resolveNoteCfiCandidate = (
  cfiRange: string,
  label: "raw" | "adjusted",
  iframeDoc: Document,
  noteText: string,
): ResolvedNoteCfiCandidate => {
  let range: Range | null = null;
  let resolvedWith: ResolvedNoteCfiCandidate["resolvedWith"] = "unresolved";

  try {
    range = new EpubCFI(cfiRange).toRange(iframeDoc, "epubjs-ignore");
    if (range) {
      resolvedWith = "epubjs-ignore";
    }
  } catch (_error) {}

  if (!range) {
    try {
      range = new EpubCFI(cfiRange).toRange(iframeDoc, "");
      if (range) {
        resolvedWith = "none";
      }
    } catch (_error) {}
  }

  let resolvedText = "";
  if (range) {
    try {
      resolvedText = range.toString();
    } catch (_error) {}
  }

  const rectCount = rangeRectCount(range);
  return {
    cfiRange,
    endOffset: range?.endOffset ?? null,
    endSnippet: excerptNodeAroundOffset(
      range?.endContainer,
      range?.endOffset ?? null,
    ),
    label,
    normalizedText: normalizeTextForComparison(resolvedText),
    rectCount,
    resolvedText: excerptText(resolvedText),
    resolvedWith,
    score: scoreResolvedCandidate(noteText, resolvedText, rectCount),
    startOffset: range?.startOffset ?? null,
    startSnippet: excerptNodeAroundOffset(
      range?.startContainer,
      range?.startOffset ?? null,
    ),
  };
};

const patchOffsetIgnoringInlineNoteMarkers = (
  anchor: Node,
  offset: number,
  ignoreClass: string,
): number => {
  let currentNode: Node = anchor;
  let totalOffset = offset;

  while (currentNode.nodeType === Node.ELEMENT_NODE && currentNode.firstChild) {
    currentNode = currentNode.firstChild;
  }

  if (currentNode.nodeType !== Node.TEXT_NODE) {
    throw new Error("No text node found within the anchor element");
  }

  const parentElement = isElementNode(currentNode.parentNode)
    ? currentNode.parentNode
    : null;
  if (parentElement?.classList.contains(ignoreClass)) {
    currentNode = parentElement;
  }

  while (currentNode.previousSibling) {
    const previousSibling = currentNode.previousSibling;

    if (previousSibling.nodeType === Node.ELEMENT_NODE) {
      const previousElement = previousSibling as Element;
      if (!previousElement.classList.contains(ignoreClass)) {
        break;
      }

      // Inline notes are reader UI, not source text, so they must not advance
      // later highlight offsets inside the same paragraph. Reaction spacers
      // also reserve layout width without representing source text.
      if (
        previousElement.getAttribute(INLINE_NOTE_MARKER_ATTRIBUTE) !== "true" &&
        previousElement.getAttribute(REACTION_SPACER_ATTRIBUTE) !== "true"
      ) {
        totalOffset += previousElement.textContent?.length || 0;
      }
    } else {
      totalOffset += previousSibling.textContent?.length || 0;
    }

    currentNode = previousSibling;
  }

  return totalOffset;
};

/**
 * Adjusts the CFI range to account for epubjs-ignore elements.
 *
 * @param cfiRange - The original CFI range to adjust.
 * @param iframeDoc - The document of the iframe containing the ebook content.
 * @returns The adjusted CFI range, or the original if no adjustment was needed.
 */
export const adjustCfiRange = (
  cfiRange: string,
  iframeDoc: Document,
): string => {
  const cfi = new EpubCFI(cfiRange, "epubjs-ignore");
  const range = cfi.toRange(iframeDoc, "epubjs-ignore");
  if (!range) return cfiRange;

  const startContainer = range.startContainer;
  const endContainer = range.endContainer;

  //   if (startContainer.nodeType !== Node.TEXT_NODE || endContainer.nodeType !== Node.TEXT_NODE) {
  //     return cfiRange;
  //   }

  //   let startOffset = 0;
  //   let endOffset = 0;

  //   // Adjust start offset
  //   let node = startContainer.previousSibling;
  //   while (node) {
  //     if (node.nodeType === Node.ELEMENT_NODE && (node as Element).classList.contains('epubjs-ignore')) {
  //       startOffset += (node as Element).textContent?.length || 0;
  //     }
  //     node = node.previousSibling;
  //   }

  //   // Adjust end offset
  //   node = endContainer.previousSibling;
  //   while (node) {
  //     if (node.nodeType === Node.ELEMENT_NODE && (node as Element).classList.contains('epubjs-ignore')) {
  //       endOffset += (node as Element).textContent?.length || 0;
  //     }
  //     node = node.previousSibling;
  //   }

  // Create a new CFI with adjusted offsets
  const cfiParts = cfiRange.split(")")[0].split(",");
  const [sharedPart, startPart, endPart] = cfiParts;
  const [startNode, startCharLoc] = startPart.split(":");
  const [endNode, endCharLoc] = endPart.split(":");
  //   const adjustedStartOffset = parseInt(startCharLoc) + startOffset;
  //   const adjustedEndOffset = parseInt(endCharLoc) + endOffset;
  const adjustedStartOffset = patchOffsetIgnoringInlineNoteMarkers(
    startContainer,
    parseInt(startCharLoc),
    "epubjs-ignore",
  );
  const adjustedEndOffset = patchOffsetIgnoringInlineNoteMarkers(
    endContainer,
    parseInt(endCharLoc),
    "epubjs-ignore",
  );

  if (
    adjustedStartOffset !== parseInt(startCharLoc) ||
    adjustedEndOffset !== parseInt(endCharLoc)
  ) {
    return `${sharedPart},${startNode}:${adjustedStartOffset},${endNode}:${adjustedEndOffset})`;
  }

  return cfiRange;
};

export const resolveBestNoteCfiRange = (
  note: Pick<Note, "id" | "cfiRange" | "text">,
  iframeDoc: Document,
  source = "unknown",
  logAlways = false,
): string => {
  const adjustedCfiRange = adjustCfiRange(note.cfiRange, iframeDoc);
  const inlineNoteMarkers = Array.from(
    iframeDoc.querySelectorAll(INLINE_NOTE_MARKER_SELECTOR),
  ) as HTMLElement[];
  const reactionSpacers = Array.from(
    iframeDoc.querySelectorAll(REACTION_SPACER_SELECTOR),
  ) as HTMLElement[];

  if (inlineNoteMarkers.length === 0 && reactionSpacers.length === 0) {
    return adjustedCfiRange;
  }
  if (adjustedCfiRange === note.cfiRange && !logAlways) {
    return note.cfiRange;
  }

  const rawCandidate = resolveNoteCfiCandidate(
    note.cfiRange,
    "raw",
    iframeDoc,
    note.text || "",
  );
  const adjustedCandidate =
    adjustedCfiRange !== note.cfiRange
      ? resolveNoteCfiCandidate(
          adjustedCfiRange,
          "adjusted",
          iframeDoc,
          note.text || "",
        )
      : undefined;

  const candidates = [rawCandidate, adjustedCandidate].filter(
    (candidate): candidate is ResolvedNoteCfiCandidate => !!candidate,
  );
  const chosenCandidate = candidates.reduce<ResolvedNoteCfiCandidate>(
    (best, candidate) => {
      if (candidate.score > best.score) {
        return candidate;
      }
      if (candidate.score === best.score && candidate.label === "raw") {
        return candidate;
      }
      return best;
    },
    rawCandidate,
  );

  const shouldLog =
    logAlways ||
    adjustedCfiRange !== note.cfiRange ||
    chosenCandidate.label !== "raw" ||
    rawCandidate.normalizedText !==
      (adjustedCandidate?.normalizedText || rawCandidate.normalizedText);

  if (shouldLog) {
    console.debug(
      "[inline-note-range]",
      JSON.stringify({
        source,
        noteId: note.id,
        noteText: excerptText(note.text || ""),
        inlineNoteMarkerCount: inlineNoteMarkers.length,
        inlineNoteMarkerIds: inlineNoteMarkers
          .map(
            (marker) =>
              marker.dataset.noteId || marker.id.replace(/^note-marker-/, ""),
          )
          .filter(Boolean)
          .slice(0, 8),
        reactionSpacerCount: reactionSpacers.length,
        reactionSpacerIds: reactionSpacers
          .map((spacer) => spacer.dataset.noteId || spacer.id)
          .filter(Boolean)
          .slice(0, 8),
        raw: rawCandidate,
        adjusted: adjustedCandidate || null,
        chosen: chosenCandidate.label,
      }),
    );
  }

  return chosenCandidate.cfiRange;
};

/**
 * Updates an annotation in the given annotations object.
 *
 * @param {Annotations} annotations - The annotations object to update.
 * @param {Note} note - The note containing the updated annotation details.
 */
export const updateAnnotation = async (
  annotations: Annotations,
  note: Note,
  bookMeta: BookMeta | undefined,
  iframeDoc: Document | undefined,
  onlyReveal = false,
) => {
  // Remove any existing annotation for this note.
  annotations.removeByData("id", note.id);
  const user = getCurrentUser();
  const noteStatus = getNoteStatus(note.id);

  // Always expose system-generated notes (e.g. from 'ai').
  //   if (noteStatus && noteStatus.hidden && note && note.owner === 'ai') {
  //     noteStatus.hidden = false;
  //   }

  // If the note is already marked as hidden, skip processing.
  //   if (noteStatus?.hidden) {
  //     logger.info('Note is already hidden, skipping update for note with id:', note.id);
  //     return;
  //   }

  // Retrieve the note visibility setting from the book metadata (default to "all").
  const noteVisibility = bookMeta?.settings.noteVisibility || "all";

  const color = (note.color || "").toLowerCase();
  const aiGreen = (aiUser.color || "#00ff00").toLowerCase();
  const isAiOrGreenNote =
    note.owner === aiUser.email || color === aiGreen || color === "#0f0";

  // Check if the note has comments.
  const hasComments = note.comments && Object.keys(note.comments).length > 0;
  // For parity with previous behavior, check if any comment text includes "#show".
  const hasShowTag = Object.values(note.comments || {}).some((comment) =>
    comment.text.toLowerCase().includes("#show"),
  );

  const needsCurrentLocationCheck =
    noteVisibility === "commentsUntilPassed" ||
    noteVisibility === "allUntilPassed";
  let isPastCurrentPoint = false;
  if (needsCurrentLocationCheck) {
    // Parse the note's CFI and the user's current location only when delayed
    // reveal rules actually depend on relative note position.
    const userCfi = bookMeta?.settings.location || "";
    const noteCfi = EpubCFI.prototype.parse(note.cfiRange);
    try {
      // Determine if the user's location has passed the note's position.
      // (Assuming that if the note's CFI is less than or equal to the user's CFI, then the note has been passed.)
      isPastCurrentPoint = EpubCFI.prototype.compare(noteCfi, userCfi) <= 0;
    } catch (error) {
      logger.warn(
        "Error comparing CFI ranges:",
        error,
        "note =",
        note,
        "userCfi =",
        userCfi,
        "noteCfi =",
        noteCfi,
      );
      const userProgress = bookMeta?.settings.progress || 0;
      if (userProgress && note.progress && note.progress > userProgress) {
        isPastCurrentPoint = true;
      } else {
        logger.error("Error comparing note progress as fallback:", error);
        isPastCurrentPoint = false;
      }
    }
  }

  // Determine whether the note should be hidden based on the note visibility setting.
  let shouldHide = false;
  switch (noteVisibility) {
    case "none":
      // Never show any highlights.
      shouldHide = true;
      break;
    case "onlyMine":
      // Only show notes created by the current user.
      if (!user || note.owner !== user.email) {
        shouldHide = true;
      }
      break;
    case "commentsUntilPassed":
      // For comments-only delayed reveal: require that the note has comments and that the user has passed the note.
      if ((!hasComments && !hasShowTag) || !isPastCurrentPoint) {
        shouldHide = true;
      }
      break;
    case "comments":
      // Reveal notes with comments immediately; hide if no comments.
      if (!hasComments && !hasShowTag) {
        shouldHide = true;
      }
      break;
    case "allUntilPassed":
      // For all-notes delayed reveal: only reveal when the user's location has passed.
      if (!isPastCurrentPoint) {
        shouldHide = true;
      }
      break;
    case "all":
      // Reveal all notes immediately.
      shouldHide = false;
      break;
    default:
      // Fallback: reveal the note.
      shouldHide = false;
      break;
  }

  // AI/green notes are always visible, except when note visibility is explicitly set to 'none'.
  if (noteVisibility !== "none" && isAiOrGreenNote) {
    shouldHide = false;
  }

  const isDelayedReveal =
    noteVisibility === "commentsUntilPassed" ||
    noteVisibility === "allUntilPassed";
  const isScrolledDelayedReveal =
    bookMeta?.settings.flow === "scrolled" && isDelayedReveal;
  // Once a delayed-reveal note has been explicitly revealed (hidden=false),
  // keep it visible across redraws in both paginated and scrolled flows.
  const alreadyRevealedInDelayedFlow =
    isDelayedReveal && noteStatus?.hidden === false;
  const shouldHideNow =
    shouldHide && !onlyReveal && !alreadyRevealedInDelayedFlow;

  // In scrolled delayed reveal, default to hidden unless we've explicitly revealed it.
  if (
    isScrolledDelayedReveal &&
    !noteStatus &&
    !onlyReveal &&
    !isAiOrGreenNote
  ) {
    setNoteStatus(note.id, { read: false, hidden: true });
    clearReactionArtifacts(note.id, iframeDoc, "scrolledDelayedRevealDefaultHidden");
    return;
  }

  if (shouldHideNow) {
    if (!noteStatus?.hidden) {
      logger.info("Hiding note with id:", note.id);
      setNoteStatus(note.id, { read: false, hidden: true });
    }
    clearReactionArtifacts(note.id, iframeDoc, "noteHiddenByVisibility", {
      noteVisibility,
      onlyReveal,
      alreadyRevealedInDelayedFlow,
    });
    return;
  }

  if (isScrolledDelayedReveal && noteStatus?.hidden) {
    if (isAiOrGreenNote) {
      setNoteStatus(note.id, { read: noteStatus.read ?? true, hidden: false });
    } else {
      clearReactionArtifacts(note.id, iframeDoc, "scrolledDelayedRevealStillHidden");
      return;
    }
  }

  if (
    !isScrolledDelayedReveal &&
    noteStatus?.hidden &&
    !onlyReveal &&
    !shouldHide
  ) {
    const defaultRead = Object.keys(note.comments || {}).length === 0;
    setNoteStatus(note.id, {
      read: noteStatus?.read ?? defaultRead,
      hidden: false,
    });
  }

  // Adjust the CFI range for terminal character ranges if necessary.
  let adjustedCfiRange = note.cfiRange;
  if (iframeDoc) {
    adjustedCfiRange = resolveBestNoteCfiRange(
      note,
      iframeDoc,
      "updateAnnotation",
    );
    if (note.cfiRange !== adjustedCfiRange) {
      console.debug(
        "adjustedCfiRange from",
        note.cfiRange,
        "to",
        adjustedCfiRange,
      );
    }
  }

  const hasValidEndpointOrder = iframeDoc
    ? isCfiRangeEndpointOrderValidForDocument(adjustedCfiRange, iframeDoc)
    : isCfiRangeEndpointOrderValid(adjustedCfiRange);
  if (!hasValidEndpointOrder) {
    logger.warn("updateAnnotation: skipping invalid CFI range", {
      noteId: note.id,
      cfiRange: note.cfiRange,
      adjustedCfiRange,
      endpointOrder: getCfiRangeEndpointOrder(adjustedCfiRange),
    });
    clearReactionArtifacts(note.id, iframeDoc, "invalidCfiRange");
    return;
  }
  if (
    iframeDoc &&
    getCfiRangeEndpointOrder(adjustedCfiRange) === 1 &&
    hasValidEndpointOrder
  ) {
    logger.debug("updateAnnotation: accepting DOM-resolved legacy CFI range", {
      noteId: note.id,
      cfiRange: note.cfiRange,
      adjustedCfiRange,
    });
  }

  // Add the annotation.
  annotations.add(
    "highlight",
    adjustedCfiRange,
    note,
    undefined,
    countComments(note) > 0
      ? getNoteStatus(note.id)?.read
        ? "wr-hlc"
        : "wr-hlcu"
      : "wr-hl",
    {
      stroke: note.color,
      fill: note.color,
    },
  );

  applyReactions(
    note,
    bookMeta?.settings.fontSize,
    0,
    adjustedCfiRange,
    iframeDoc,
  );
};

const createSVG = (name: string) => {
  return document.createElementNS("http://www.w3.org/2000/svg", name);
};

const SVG_XLINK_NAMESPACE = "http://www.w3.org/1999/xlink";

const createReactionFallbackText = (
  emoji: string,
  xOffset: number,
  emojiSize: number,
) => {
  const reactionText = createSVG("text");
  reactionText.textContent = emoji;
  reactionText.setAttribute("x", `${xOffset}`);
  reactionText.setAttribute("y", "0");
  reactionText.setAttribute("font-size", `${emojiSize}px`);
  reactionText.setAttribute(
    "font-family",
    "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif",
  );
  reactionText.setAttribute("text-anchor", "middle");
  reactionText.setAttribute("dominant-baseline", "central");
  return reactionText;
};

const appendReactionEmoji = (
  reactionContainer: SVGGElement,
  note: Note,
  emoji: string,
  xOffset: number,
  emojiSize: number,
): { mode: "image" | "text"; unified: string | null } => {
  const unified = emojiToUnified(emoji);
  const imageUrl = getAppleEmojiImageUrl(emoji);
  if (!unified || !imageUrl) {
    reactionContainer.appendChild(
      createReactionFallbackText(emoji, xOffset, emojiSize),
    );
    return { mode: "text", unified };
  }

  const reactionImage = createSVG("image") as SVGImageElement;
  reactionImage.setAttribute("x", `${xOffset - emojiSize / 2}`);
  reactionImage.setAttribute("y", `${-emojiSize / 2}`);
  reactionImage.setAttribute("width", `${emojiSize}`);
  reactionImage.setAttribute("height", `${emojiSize}`);
  reactionImage.setAttribute("href", imageUrl);
  reactionImage.setAttributeNS(SVG_XLINK_NAMESPACE, "href", imageUrl);
  reactionImage.setAttribute("role", "img");
  reactionImage.setAttribute("aria-label", emoji);
  reactionImage.setAttribute("preserveAspectRatio", "xMidYMid meet");
  reactionImage.onerror = () => {
    logger.warn("applyReactions: reaction emoji image failed; falling back to text", {
      noteId: note.id,
      cfiRange: note.cfiRange,
      emoji,
      unified,
      imageUrl,
    });
    const fallbackText = createReactionFallbackText(emoji, xOffset, emojiSize);
    reactionImage.parentNode?.replaceChild(fallbackText, reactionImage);
  };
  reactionContainer.appendChild(reactionImage);
  return { mode: "image", unified };
};

const applyReactions = async (
  note: Note,
  fontSize?: number,
  attempt = 0,
  adjustedCfiRange?: string,
  iframeDoc?: Document,
) => {
  const reactionContainerSelector = getReactionContainerSelector(note.id);
  const reactionEntries = Object.entries(note.reactions || {})
    .filter(([, emoji]) => Boolean(emoji))
    .sort(([userA, emojiA], [userB, emojiB]) => {
      const userComparison = userA.localeCompare(userB);
      if (userComparison !== 0) {
        return userComparison;
      }
      return emojiA.localeCompare(emojiB);
    });
  const reactionSignature = getReactionSignature(note.reactions);
  const noteStatus = getNoteStatus(note.id);
  const reactionBadgeDismissed =
    noteStatus?.reactionsHidden &&
    noteStatus.reactionsHiddenSignature === reactionSignature;

  if (reactionEntries.length === 0) {
    clearReactionArtifacts(note.id, iframeDoc, "emptyReactionSet");
    return;
  }
  if (reactionBadgeDismissed) {
    clearReactionArtifacts(note.id, iframeDoc, "reactionBadgeDismissed", {
      reactionSignature,
      reactionCount: reactionEntries.length,
    });
    return;
  }
  if (
    noteStatus?.reactionsHidden &&
    noteStatus.reactionsHiddenSignature !== reactionSignature
  ) {
    setNoteStatus(note.id, {
      read: noteStatus.read ?? Object.keys(note.comments || {}).length === 0,
      hidden: noteStatus.hidden,
      reactionsHidden: false,
      reactionsHiddenSignature: reactionSignature,
    });
    logger.debug("applyReactions: restoring dismissed badge after reaction change", {
      noteId: note.id,
      cfiRange: note.cfiRange,
      previousReactionSignature: noteStatus.reactionsHiddenSignature,
      reactionSignature,
      reactionCount: reactionEntries.length,
    });
  }

  const selectorsTried = buildHighlightReactionAnchorSelectors(
    note.id,
    note.cfiRange,
  );
  const anchorGroup = findHighlightReactionAnchorGroup(
    document,
    note.id,
    note.cfiRange,
  );
  if (!anchorGroup) {
    if (attempt < 1) {
      logger.debug("applyReactions: anchor group missing; scheduling retry", {
        noteId: note.id,
        cfiRange: note.cfiRange,
        attempt,
        selectorsTried,
      });
      requestAnimationFrame(() => {
        void applyReactions(
          note,
          fontSize,
          attempt + 1,
          adjustedCfiRange,
          iframeDoc,
        );
      });
      return;
    }
    logger.warn("applyReactions: unable to locate highlight anchor group", {
      noteId: note.id,
      cfiRange: note.cfiRange,
      attempt,
      selectorsTried,
      svgCount: document.querySelectorAll(".epub-view > svg").length,
    });
    clearReactionArtifacts(note.id, iframeDoc, "anchorGroupMissing", {
      attempt,
      selectorsTried,
    });
    return;
  }

  const svg = anchorGroup.closest("svg");
  if (!(svg instanceof SVGSVGElement)) {
    logger.warn("applyReactions: anchor group is not inside an SVG root", {
      noteId: note.id,
      cfiRange: note.cfiRange,
    });
    return;
  }

  document.querySelectorAll(reactionContainerSelector).forEach((container) => {
    if (container.parentNode !== svg) {
      container.remove();
    }
  });

  let reactionContainer = svg.querySelector(
    reactionContainerSelector,
  ) as SVGGElement | null;
  if (!reactionContainer) {
    reactionContainer = createSVG("g") as SVGGElement;
    svg.appendChild(reactionContainer);
  } else {
    while (reactionContainer.firstChild) {
      reactionContainer.removeChild(reactionContainer.firstChild);
    }
  }
  reactionContainer.setAttribute("data-reaction-note-id", note.id);
  reactionContainer.setAttribute("reaction-epubcfi", note.cfiRange);
  reactionContainer.setAttribute("pointer-events", "none");

  const highlightRects = sortHighlightRects(
    anchorGroup.querySelectorAll("rect") as NodeListOf<SVGRectElement>,
  );
  if (highlightRects.length === 0) {
    if (attempt < 1) {
      logger.debug("applyReactions: anchor group has no rects; scheduling retry", {
        noteId: note.id,
        cfiRange: note.cfiRange,
        attempt,
      });
      requestAnimationFrame(() => {
        void applyReactions(
          note,
          fontSize,
          attempt + 1,
          adjustedCfiRange,
          iframeDoc,
        );
      });
      return;
    }
    logger.warn("applyReactions: highlight anchor group had no rects", {
      noteId: note.id,
      cfiRange: note.cfiRange,
      attempt,
    });
    clearReactionArtifacts(note.id, iframeDoc, "highlightAnchorGroupHadNoRects", {
      attempt,
    });
    return;
  }

  const lastRect = highlightRects[0];
  const bottomRightX =
    parseFloat(lastRect.getAttribute("x") || "0") +
    parseFloat(lastRect.getAttribute("width") || "0");
  const lineCenterY =
    parseFloat(lastRect.getAttribute("y") || "0") +
    parseFloat(lastRect.getAttribute("height") || "0") / 2;
  const { emojiSize, itemGap, itemWidth, pillPaddingX, pillHeight, highlightGap } =
    getReactionBadgeMetrics(
      !fontSize || fontSize === defaultBookSettings.fontSize
        ? undefined
        : fontSize,
    );
  const pillWidth =
    pillPaddingX * 2 +
    reactionEntries.length * itemWidth +
    Math.max(0, reactionEntries.length - 1) * itemGap;
  const spacer = syncReactionSpacer(
    note,
    iframeDoc,
    adjustedCfiRange || note.cfiRange,
    pillWidth + highlightGap,
  );
  const spacerRect = spacer?.getBoundingClientRect();
  const followingContentRect = spacer
    ? getFirstRenderableRectForNode(spacer.nextSibling)
    : null;
  const svgWidth =
    svg.getBoundingClientRect().width ||
    parseFloat(svg.getAttribute("width") || "0");
  const frameElement = iframeDoc?.defaultView?.frameElement as HTMLElement | null;
  const frameRect = frameElement?.getBoundingClientRect();
  const clipContainerRect =
    frameElement?.parentElement?.parentElement?.getBoundingClientRect();
  const visibleLeft =
    frameRect && clipContainerRect
      ? clipContainerRect.left - frameRect.left
      : undefined;
  const visibleRight =
    frameRect && clipContainerRect
      ? clipContainerRect.right - frameRect.left
      : undefined;
  const {
    preferredX,
    lineCenterY: resolvedLineCenterY,
    usesFollowingContentLineCenter,
    followingContentContinuesAfterSpacer,
  } = resolveReactionBadgePreferredPlacement({
    fallbackX: bottomRightX + highlightGap,
    fallbackCenterY: lineCenterY,
    pillWidth,
    spacerLeft: spacerRect?.left,
    spacerWidth: spacerRect?.width,
    followingContentLeft: followingContentRect?.left,
    followingContentLineCenterY: followingContentRect
      ? followingContentRect.top + followingContentRect.height / 2
      : undefined,
  });
  const clampBounds = resolveReactionBadgeClampBounds({
    anchorLeft: parseFloat(lastRect.getAttribute("x") || "0"),
    anchorRight: bottomRightX,
    visibleLeft,
    visibleRight,
  });
  const clampedX = calculateReactionBadgeX({
    preferredX,
    pillWidth,
    svgWidth,
    visibleLeft: clampBounds.appliedVisibleLeft,
    visibleRight: clampBounds.appliedVisibleRight,
  });

  reactionContainer.setAttribute(
    "transform",
    `translate(${clampedX}, ${resolvedLineCenterY})`,
  );
  reactionContainer.setAttribute(
    "style",
    "filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.22)); cursor: pointer;",
  );
  reactionContainer.setAttribute("pointer-events", "visiblePainted");

  const reactionBg = createSVG("rect");
  reactionBg.setAttribute("x", "0");
  reactionBg.setAttribute("y", `${-pillHeight / 2}`);
  reactionBg.setAttribute("width", `${pillWidth}`);
  reactionBg.setAttribute("height", `${pillHeight}`);
  reactionBg.setAttribute("rx", `${pillHeight / 2}`);
  reactionBg.setAttribute("fill", getTheme() === "dark" ? "#171717" : "#fff8eb");
  reactionBg.setAttribute("fill-opacity", getTheme() === "dark" ? "0.96" : "0.98");
  reactionBg.setAttribute("stroke", getTheme() === "dark" ? "#f4f4f4" : "#99721d");
  reactionBg.setAttribute("stroke-opacity", getTheme() === "dark" ? "0.44" : "0.36");
  reactionBg.setAttribute("stroke-width", "1.25");
  reactionContainer.appendChild(reactionBg);

  reactionContainer.onpointerdown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  reactionContainer.onpointerup = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };
  reactionContainer.onclick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentStatus = getNoteStatus(note.id);
    setNoteStatus(note.id, {
      read: currentStatus?.read ?? Object.keys(note.comments || {}).length === 0,
      hidden: currentStatus?.hidden,
      reactionsHidden: true,
      reactionsHiddenSignature: reactionSignature,
    });
    clearReactionArtifacts(note.id, iframeDoc, "badgeTappedToDismiss", {
      reactionSignature,
      reactionCount: reactionEntries.length,
    });
    logger.debug("applyReactions: dismissed badge from tap", {
      noteId: note.id,
      cfiRange: note.cfiRange,
      reactionSignature,
      reactionCount: reactionEntries.length,
    });
    window.dispatchEvent(
      new CustomEvent(REACTION_BADGE_DISMISSED_EVENT, {
        detail: {
          noteId: note.id,
          reactionSignature,
        },
      }),
    );
  };

  let xOffset = pillPaddingX + itemWidth / 2;
  const reactionEmojiRenderModes: Array<{
    emoji: string;
    unified: string | null;
    mode: "image" | "text";
  }> = [];
  reactionEntries.forEach(([_user, emoji]) => {
    const renderMode = appendReactionEmoji(
      reactionContainer,
      note,
      emoji,
      xOffset,
      emojiSize,
    );
    reactionEmojiRenderModes.push({ emoji, ...renderMode });
    xOffset += itemWidth + itemGap;
  });

  logger.debug("applyReactions: rendered reaction badge", {
    noteId: note.id,
    cfiRange: note.cfiRange,
    reactionCount: reactionEntries.length,
    rectCount: highlightRects.length,
    pillWidth,
    pillHeight,
    spacerWidth: Math.ceil(pillWidth + highlightGap),
    svgWidth,
    preferredX,
    clampedX,
    anchorLeft: parseFloat(lastRect.getAttribute("x") || "0"),
    anchorRight: bottomRightX,
    visibleLeft,
    visibleRight,
    appliedVisibleLeft: clampBounds.appliedVisibleLeft ?? null,
    appliedVisibleRight: clampBounds.appliedVisibleRight ?? null,
    anchorIntersectsVisibleBounds: clampBounds.anchorIntersectsVisibleBounds,
    spacerRect: spacerRect
      ? {
          left: spacerRect.left,
          right: spacerRect.right,
          width: spacerRect.width,
          top: spacerRect.top,
          bottom: spacerRect.bottom,
          height: spacerRect.height,
        }
      : null,
    followingContentRect: followingContentRect
      ? {
          left: followingContentRect.left,
          right: followingContentRect.right,
          top: followingContentRect.top,
          bottom: followingContentRect.bottom,
          width: followingContentRect.width,
          height: followingContentRect.height,
        }
      : null,
    frameRect: frameRect
      ? {
          left: frameRect.left,
          right: frameRect.right,
          width: frameRect.width,
        }
      : null,
    clipContainerRect: clipContainerRect
      ? {
          left: clipContainerRect.left,
          right: clipContainerRect.right,
          width: clipContainerRect.width,
        }
      : null,
    usesFollowingContentLineCenter,
    followingContentContinuesAfterSpacer,
    lineCenterY: resolvedLineCenterY,
    reactionSignature,
    reactionEmojiRenderModes,
  });
};

/**
 * Get the text of a book up to a given section number.
 * @param book - The BookMeta to get the text of.
 * @param format - The format of the text to get (text or html).
 * @param endSectionNum - The number of the section to end at.
 * @returns The text of the book up to the given section number, or the entire book if no endSectionNum is provided.
 */
export const getBookText = async (
  book: BookMeta,
  format: "text" | "html",
  endSectionNum?: number,
): Promise<string> => {
  const { sections } = await getChapters(book);
  const endSection = endSectionNum || sections.length - 1;
  const text = sections
    .slice(0, endSection + 1)
    .map((section) => (format === "text" ? section.text : section.html))
    .join("\n\n");
  //   console.debug('getBookText book=', book, 'format=', format, 'endSectionNum=', endSectionNum, 'text=', text);
  return text;
};

const mediumDelay = 250;

export const getUpdates = debounce(getUpdatesBase, mediumDelay);
export const reportOpen = debounce(reportOpenBase, mediumDelay);
export const getBookProgress = debounce(getBookProgressBase, mediumDelay);
export const getStreaks = debounce(getStreaksBase, mediumDelay);
export const fetchFollowing = debounce(fetchFollowingBase, mediumDelay);

/**
 * Organizes books based on user-specified instructions by calling the backend API
 *
 * @param {string} instructions - User's instructions for book organization (e.g., "by genre then by author")
 * @param {string[]} [bookIds] - Optional array of book IDs to organize. If not provided, all books will be organized.
 * @returns {Promise<{ [groupName: string]: any } | null>} - A hierarchical JSON structure of organized books or null if failed
 */
export const organizeBooks = async (
  instructions: string,
  bookIds?: string[],
  timeoutMs: number = 300000, // 2 minutes timeout
): Promise<{ [groupName: string]: any } | null> => {
  const isOnline = useOnlineStore.getState().isOnline;
  if (!isOnline) {
    console.error("Cannot organize books: offline");
    return null;
  }

  try {
    const payload: { instructions: string; bookIds?: string[] } = {
      instructions,
    };

    if (bookIds && bookIds.length > 0) {
      payload.bookIds = bookIds;
    }

    // Set up AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const response = await fetch("/api/organize_books", {
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    // Clear the timeout if the fetch completes before the timeout
    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error("Error organizing books:", response.statusText);
      return null;
    }

    const data = await response.json();
    return data.result;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      console.error(`Request timed out after ${timeoutMs / 1000} seconds`);
    } else {
      console.error("Error organizing books:", error);
    }
    return null;
  }
};
